import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import UploadOutlined from "@mui/icons-material/UploadOutlined";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import AddIcon from "@mui/icons-material/Add";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import CheckOutlined from "@mui/icons-material/CheckOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import ArrowBackOutlined from "@mui/icons-material/ArrowBackOutlined";
import Spinner from "common/components/base/spinner/Spinner";
import useOnClickOutside from "common/hooks/useOnClickOutside";
import useDelayedFlag from "common/hooks/useDelayedFlag";
import EntitiesGenerationShimmer from "../../components/entities-generation-shimmer/EntitiesGenerationShimmer";
import GraphCanvasOntology, {
  getNodeColor
} from "./components/graph-canvas/GraphCanvasOntology";
import {
  GraphNode,
  GraphEdge
} from "modules/ontology/data/ontology-graph.model";
import {
  ONTOLOGY_WORKBENCH_STEPS,
  CompetencyQuestionRow
} from "../../data/create-ontology-questions.data";
import {
  OntologyService,
  OntologyBusinessQuestionItem,
  OntologyNodeTraceabilityBusinessQuestion,
  OntologyNodeTraceabilityCompetencyQuestion,
  EntitiesRelationshipsQueryFilters,
  ListPaginationMeta
} from "../../data/ontology.service";
import {
  ConceptRow,
  AttributeRow,
  RelationshipRow,
  EntitiesSummary,
  EntitiesSummaryByType,
  ConceptStatus
} from "../../data/create-ontology-entities-relationships.data";
import MultiSelectDropdown from "../../components/multi-select-dropdown";
import {
  useRelatedEntitiesGraph,
  RelatedEntitiesGraphOverlay,
  RelatedEntitiesPanel
} from "./components/related-entities-graph";
import "./CreateOntologyEntitiesRelationships.scss";

type ContentTab = "concepts" | "attributes" | "relationships";

const EMPTY_LIST_PAGINATION: ListPaginationMeta & { isLoadingMore: boolean } = {
  page: 1,
  totalPages: 1,
  totalRecords: 0,
  isLoadingMore: false
};

const ENTITIES_GENERATION_POLL_INTERVAL_MS = 20000;
const ENTITIES_GENERATION_POLL_TIMEOUT_MS = 800000;
// Stages at/after which entities (Concepts/Attributes/Relationships) are confirmed generated;
// any earlier stage (e.g. CQ_GENERATED, "Entity Generation in progress") means generation is
// still running. "Entities Generated" is the actual backend stage name returned once the
// entities-relationships graphndpoint has data to serve.
const ENTITIES_READY_STAGES = [
  "Entities Generated",
  "Entities & Relationships",
  "Reuse & Harmonisation",
  "Ground to Documents",
  "Build Knowledge Graph",
  "Validate & Release"
];
// Backend stage name when entity/relationship generation failed; the user is sent back to the
// Competency Questions screen to review/regenerate.
const ENTITY_GENERATION_FAILED_STAGE = "Entity Generation Failed";

// Polls the initiative until its stage reaches one of the ready stages, returning the stage it
// eventually lands on (or null if the timeout elapses or `shouldContinue` turns false first —
// e.g. because the component unmounted or the effect re-ran — so the loop doesn't keep hitting
// the API in the background after it's no longer needed).
const waitForEntitiesGeneration = async (
  initiativeId: string,
  shouldContinue: () => boolean
): Promise<string | null> => {
  const deadline = Date.now() + ENTITIES_GENERATION_POLL_TIMEOUT_MS;
  while (Date.now() < deadline && shouldContinue()) {
    const initiative = await OntologyService.getInitiativeById(initiativeId);
    if (
      ENTITIES_READY_STAGES.includes(initiative.currentStageName) ||
      initiative.currentStageName === ENTITY_GENERATION_FAILED_STAGE
    ) {
      return initiative.currentStageName;
    }
    if (!shouldContinue()) return null;
    await new Promise((resolve) =>
      setTimeout(resolve, ENTITIES_GENERATION_POLL_INTERVAL_MS)
    );
  }
  return null;
};

const DESCRIPTION_WORD_LIMIT = 20;

// Truncates a description to its first N words; the full text is still shown via the
// native title tooltip on hover.
const truncateDescription = (text: string): string => {
  const words = text.trim().split(/\s+/);
  if (words.length <= DESCRIPTION_WORD_LIMIT) return text;
  return `${words.slice(0, DESCRIPTION_WORD_LIMIT).join(" ")}…`;
};

// Renders codes (CQ IDs or BQ IDs) as chips, one per row (see .create-entities-cq-chip-group).
// When visibleLimit is given and exceeded, the last slot collapses into a "+N" overflow chip
// whose tooltip lists the remaining codes.
const renderCodeChips = (
  codes: string[],
  keyPrefix: string,
  visibleLimit?: number
) => {
  if (codes.length === 0) {
    return <span className="create-entities-cq-pill">-</span>;
  }
  const hasOverflow = visibleLimit != null && codes.length > visibleLimit;
  const visibleCodes = hasOverflow ? codes.slice(0, visibleLimit! - 1) : codes;
  return (
    <div className="create-entities-cq-chip-group">
      {visibleCodes.map((code, index) => (
        <span key={`${keyPrefix}-${index}`} className="create-entities-cq-pill">
          {code}
        </span>
      ))}
      {hasOverflow && (
        <span
          className="create-entities-cq-pill create-entities-cq-pill--overflow"
          title={codes.slice(visibleLimit! - 1).join(", ")}
        >
          +{codes.length - (visibleLimit! - 1)}
        </span>
      )}
    </div>
  );
};

const CQ_CHIP_VISIBLE_LIMIT = 4;
const BQ_CHIP_VISIBLE_LIMIT = 4;

const STATUS_CLASS: Record<ConceptStatus, string> = {
  Draft: "create-entities-status-pill--draft",
  Accepted: "create-entities-status-pill--accepted",
  "Needs Review": "create-entities-status-pill--needs-review",
  Rejected: "create-entities-status-pill--rejected"
};

// Maps a row's status to the matching EntitiesSummary bucket, so local status edits
// (bulk accept/reject) can keep the per-tab summary counts in sync without a refetch.
const STATUS_SUMMARY_KEY: Record<ConceptStatus, keyof EntitiesSummary> = {
  Draft: "draft",
  Accepted: "approved",
  "Needs Review": "needsReview",
  Rejected: "rejected"
};

const STATUS_OPTIONS: ConceptStatus[] = [
  "Draft",
  "Accepted",
  "Needs Review",
  "Rejected"
];

// Slug values expected by the backend's `?status=` query param.
const STATUS_QUERY_VALUE: Record<ConceptStatus, string> = {
  Draft: "draft",
  Accepted: "accepted",
  "Needs Review": "needs_review",
  Rejected: "rejected"
};

// Entity-category values expected by the backend's `?nodeType=` query param.
const ENTITY_TYPE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: "node", label: "Concepts" },
  { value: "attribute", label: "Attributes" },
  { value: "relationship", label: "Relationships" }
];

// Local, editable snapshot of whichever entity is open in the Modify drawer; only the
// fields relevant to the active type are populated/shown.
interface ModifyFormState {
  nodeName?: string;
  nodeType?: string;
  status?: ConceptStatus;
  description?: string;
  attribute?: string;
  sourceNode?: string;
  relationshipType?: string;
  targetNode?: string;
}

// Editable snapshot for the Add drawer; fields shown depend on which tab was active
// when the drawer was opened (Concepts/Attributes/Relationships).
interface AddFormState {
  businessQuestionIds?: string[];
  competencyQuestionIds?: string[];
  nodeName?: string;
  nodeType?: string;
  description?: string;
  nodeId?: string;
  attribute?: string;
  sourceNode?: string;
  relationshipType?: string;
  targetNode?: string;
}

const CreateOntologyEntitiesRelationships: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initiativeId = (location.state as { initiativeId?: string } | null)
    ?.initiativeId;

  const [activeTab, setActiveTab] = useState<ContentTab>("concepts");
  const [viewMode, setViewMode] = useState<"table" | "graph">("table");
  const [graphNodes, setGraphNodes] = useState<GraphNode[]>([]);
  const [graphEdges, setGraphEdges] = useState<GraphEdge[]>([]);
  const [isLoadingGraph, setIsLoadingGraph] = useState(false);
  const [selectedGraphNodeId, setSelectedGraphNodeId] = useState<string | null>(
    null
  );
  // Filter text for the Graph Directory panel's node list (Graph View, right-hand side).
  const [graphDirectorySearch, setGraphDirectorySearch] = useState("");
  // Which tab of the Graph Directory panel is active; switches to "details" automatically
  // when a node is selected on the canvas (replaces the old View-drawer-on-click behavior).
  const [graphDirectoryTab, setGraphDirectoryTab] = useState<
    "nodes" | "details"
  >("nodes");
  const [selectedNodeDetail, setSelectedNodeDetail] = useState<{
    conceptRow: ConceptRow;
    businessQuestions: OntologyNodeTraceabilityBusinessQuestion[];
    competencyQuestions: OntologyNodeTraceabilityCompetencyQuestion[];
  } | null>(null);
  const [isLoadingNodeDetail, setIsLoadingNodeDetail] = useState(false);
  // The selected node's own attributes, fetched directly (not name-matched against the
  // paginated attributeRows table, which only ever holds one page of ALL attributes).
  const [selectedNodeAttributes, setSelectedNodeAttributes] = useState<
    AttributeRow[]
  >([]);
  // Double-clicking a graph node opens the "Related Entities" ego-subgraph (focused node +
  // its connected nodes); fully encapsulated so drilling into another node from inside the
  // overlay never touches the main canvas's own state.
  const relatedEntities = useRelatedEntitiesGraph(initiativeId);
  const [isLoading, setIsLoading] = useState(false);
  // True while the initiative's stage reports entities generation still in progress.
  const [isGeneratingEntities, setIsGeneratingEntities] = useState(false);
  // Only shows the shimmer once generation has been running for 2s, so a stage check that
  // resolves quickly (entities already generated) never flashes it.
  const showEntitiesGenerationShimmer = useDelayedFlag(
    isGeneratingEntities,
    2000
  );
  // Set when the initiative's stage is "Entity Generation Failed"; shows a retry message
  // with a link back to the Competency Questions screen instead of the table/graph views.
  const [entitiesGenerationFailed, setEntitiesGenerationFailed] =
    useState(false);
  // True while triggering reuse/harmonize analysis and polling for it to finish.
  const [isTriggeringHarmonization, setIsTriggeringHarmonization] =
    useState(false);
  const [harmonizationError, setHarmonizationError] = useState<string | null>(
    null
  );
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );
  // True once concept/attribute/relationship data has been fetched at least once; gates the
  // summary row so it doesn't flash default zero values before the real data arrives.
  const [hasLoadedConcepts, setHasLoadedConcepts] = useState(false);
  const [conceptRows, setConceptRows] = useState<ConceptRow[]>([]);
  const [attributeRows, setAttributeRows] = useState<AttributeRow[]>([]);
  const [relationshipRows, setRelationshipRows] = useState<RelationshipRow[]>(
    []
  );
  // Server-side pagination state per tab, driving true infinite scroll (fetching the next
  // page from the API) rather than just revealing already-loaded rows.
  const [conceptsPagination, setConceptsPagination] = useState<
    ListPaginationMeta & { isLoadingMore: boolean }
  >(EMPTY_LIST_PAGINATION);
  const [attributesPagination, setAttributesPagination] = useState<
    ListPaginationMeta & { isLoadingMore: boolean }
  >(EMPTY_LIST_PAGINATION);
  const [relationshipsPagination, setRelationshipsPagination] = useState<
    ListPaginationMeta & { isLoadingMore: boolean }
  >(EMPTY_LIST_PAGINATION);
  const [summary, setSummary] = useState<EntitiesSummaryByType>({
    nodes: { total: 0, draft: 0, approved: 0, needsReview: 0, rejected: 0 },
    attributes: {
      total: 0,
      draft: 0,
      approved: 0,
      needsReview: 0,
      rejected: 0
    },
    relationships: {
      total: 0,
      draft: 0,
      approved: 0,
      needsReview: 0,
      rejected: 0
    }
  });
  const [conceptSelectedIds, setConceptSelectedIds] = useState<string[]>([]);
  const [attributeSelectedIds, setAttributeSelectedIds] = useState<string[]>(
    []
  );
  const [relationshipSelectedIds, setRelationshipSelectedIds] = useState<
    string[]
  >([]);
  const [viewDrawerEntity, setViewDrawerEntity] = useState<{
    type: ContentTab;
    row: ConceptRow | AttributeRow | RelationshipRow;
  } | null>(null);
  const [viewDrawerTraceability, setViewDrawerTraceability] = useState<{
    businessQuestions: OntologyNodeTraceabilityBusinessQuestion[];
    competencyQuestions: OntologyNodeTraceabilityCompetencyQuestion[];
  } | null>(null);
  const [isLoadingViewDrawer, setIsLoadingViewDrawer] = useState(false);

  // Opens the drawer immediately with the row already loaded in the table; for Concepts,
  // also fetches the real node detail + traceability from the backend to refresh it.
  const openViewDrawer = (
    type: ContentTab,
    row: ConceptRow | AttributeRow | RelationshipRow
  ) => {
    setViewDrawerEntity({ type, row });
    setViewDrawerTraceability(null);

    if (type === "concepts" && initiativeId) {
      setIsLoadingViewDrawer(true);
      OntologyService.getNodeDetail(initiativeId, row.id)
        .then((detail) => {
          setViewDrawerEntity({ type: "concepts", row: detail.conceptRow });
          setViewDrawerTraceability({
            businessQuestions: detail.businessQuestions,
            competencyQuestions: detail.competencyQuestions
          });
        })
        .finally(() => setIsLoadingViewDrawer(false));
    } else if (type === "attributes" && initiativeId) {
      setIsLoadingViewDrawer(true);
      const nodeNameById = new Map(
        conceptRows.map((conceptRow) => [conceptRow.id, conceptRow.nodeName])
      );
      OntologyService.getAttributeDetail(initiativeId, row.id, nodeNameById)
        .then((detail) => {
          setViewDrawerEntity({
            type: "attributes",
            row: detail.attributeRow
          });
          setViewDrawerTraceability({
            businessQuestions: detail.businessQuestions,
            competencyQuestions: detail.competencyQuestions
          });
        })
        .finally(() => setIsLoadingViewDrawer(false));
    } else if (type === "relationships" && initiativeId) {
      setIsLoadingViewDrawer(true);
      const nodeNameById = new Map(
        conceptRows.map((conceptRow) => [conceptRow.id, conceptRow.nodeName])
      );
      OntologyService.getRelationshipDetail(initiativeId, row.id, nodeNameById)
        .then((detail) => {
          setViewDrawerEntity({
            type: "relationships",
            row: detail.relationshipRow
          });
          setViewDrawerTraceability({
            businessQuestions: detail.businessQuestions,
            competencyQuestions: detail.competencyQuestions
          });
        })
        .finally(() => setIsLoadingViewDrawer(false));
    }
  };

  const closeViewDrawer = () => {
    setViewDrawerEntity(null);
    setViewDrawerTraceability(null);
  };

  const [modifyDrawerEntity, setModifyDrawerEntity] = useState<{
    type: ContentTab;
    row: ConceptRow | AttributeRow | RelationshipRow;
  } | null>(null);
  const [modifyDrawerTraceability, setModifyDrawerTraceability] = useState<{
    businessQuestions: OntologyNodeTraceabilityBusinessQuestion[];
    competencyQuestions: OntologyNodeTraceabilityCompetencyQuestion[];
  } | null>(null);
  const [isLoadingModifyDrawer, setIsLoadingModifyDrawer] = useState(false);
  const [isSavingModify, setIsSavingModify] = useState(false);
  const [modifyForm, setModifyForm] = useState<ModifyFormState>({});

  // Builds the editable form snapshot for whichever row is being modified.
  const buildModifyForm = (
    type: ContentTab,
    row: ConceptRow | AttributeRow | RelationshipRow
  ): ModifyFormState => {
    if (type === "concepts") {
      const conceptRow = row as ConceptRow;
      return {
        nodeName: conceptRow.nodeName,
        nodeType: conceptRow.nodeTypeRaw,
        status: conceptRow.status,
        description: conceptRow.description
      };
    }
    if (type === "attributes") {
      const attributeRow = row as AttributeRow;
      return {
        attribute: attributeRow.attribute,
        status: attributeRow.status,
        description: attributeRow.description
      };
    }
    const relationshipRow = row as RelationshipRow;
    return {
      sourceNode: relationshipRow.sourceNode,
      relationshipType: relationshipRow.relationshipType,
      targetNode: relationshipRow.targetNode
    };
  };

  // Opens the Modify drawer immediately with the row already loaded in the table, then
  // refreshes it with the real detail + traceability from the backend (same endpoints as View).
  const openModifyDrawer = (
    type: ContentTab,
    row: ConceptRow | AttributeRow | RelationshipRow
  ) => {
    setModifyDrawerEntity({ type, row });
    setModifyForm(buildModifyForm(type, row));
    setModifyDrawerTraceability(null);

    if (!initiativeId) return;
    const nodeNameById = new Map(
      conceptRows.map((conceptRow) => [conceptRow.id, conceptRow.nodeName])
    );
    setIsLoadingModifyDrawer(true);
    if (type === "concepts") {
      OntologyService.getNodeDetail(initiativeId, row.id)
        .then((detail) => {
          setModifyDrawerEntity({ type: "concepts", row: detail.conceptRow });
          setModifyForm(buildModifyForm("concepts", detail.conceptRow));
          setModifyDrawerTraceability({
            businessQuestions: detail.businessQuestions,
            competencyQuestions: detail.competencyQuestions
          });
        })
        .finally(() => setIsLoadingModifyDrawer(false));
    } else if (type === "attributes") {
      OntologyService.getAttributeDetail(initiativeId, row.id, nodeNameById)
        .then((detail) => {
          setModifyDrawerEntity({
            type: "attributes",
            row: detail.attributeRow
          });
          setModifyForm(buildModifyForm("attributes", detail.attributeRow));
          setModifyDrawerTraceability({
            businessQuestions: detail.businessQuestions,
            competencyQuestions: detail.competencyQuestions
          });
        })
        .finally(() => setIsLoadingModifyDrawer(false));
    } else {
      OntologyService.getRelationshipDetail(initiativeId, row.id, nodeNameById)
        .then((detail) => {
          setModifyDrawerEntity({
            type: "relationships",
            row: detail.relationshipRow
          });
          setModifyForm(
            buildModifyForm("relationships", detail.relationshipRow)
          );
          setModifyDrawerTraceability({
            businessQuestions: detail.businessQuestions,
            competencyQuestions: detail.competencyQuestions
          });
        })
        .finally(() => setIsLoadingModifyDrawer(false));
    }
  };

  const closeModifyDrawer = () => {
    setModifyDrawerEntity(null);
    setModifyDrawerTraceability(null);
    setModifyForm({});
  };

  const updateModifyForm = <K extends keyof ModifyFormState>(
    key: K,
    value: ModifyFormState[K]
  ) => {
    setModifyForm((prev) => ({ ...prev, [key]: value }));
  };

  // Commits the edited fields via the real PATCH /entities endpoint for all three types.
  // "Save Draft" forces the row back to the backend's "Draft" status; "Apply Changes" keeps
  // whatever status was selected in the form.
  const saveModifyDrawer = async (asDraft: boolean) => {
    if (!modifyDrawerEntity) return;
    const { type, row } = modifyDrawerEntity;
    setIsSavingModify(true);

    if (type === "concepts") {
      const status = asDraft ? "Draft" : modifyForm.status ?? row.status;
      const nodeName = modifyForm.nodeName ?? (row as ConceptRow).nodeName;
      const nodeType = (
        modifyForm.nodeType ?? (row as ConceptRow).nodeTypeRaw
      ).trim();
      const description = modifyForm.description ?? row.description;

      if (initiativeId) {
        try {
          await OntologyService.updateEntity(initiativeId, "node", row.id, {
            nodeName,
            nodeType,
            description,
            status
          });
        } catch {
          setIsSavingModify(false);
          return;
        }
      }

      setConceptRows((prev) =>
        prev.map((item) =>
          item.id === row.id
            ? {
                ...item,
                nodeName,
                nodeType: nodeType.toUpperCase().includes("DOC")
                  ? "Document"
                  : "Class",
                nodeTypeRaw: nodeType,
                description,
                status
              }
            : item
        )
      );
    } else if (type === "attributes") {
      const status = asDraft ? "Draft" : modifyForm.status ?? row.status;
      const attribute = modifyForm.attribute ?? (row as AttributeRow).attribute;
      const description = modifyForm.description ?? row.description;

      if (initiativeId) {
        try {
          await OntologyService.updateEntity(
            initiativeId,
            "attribute",
            row.id,
            {
              attributeName: attribute,
              description,
              status
            }
          );
        } catch {
          setIsSavingModify(false);
          return;
        }
      }

      setAttributeRows((prev) =>
        prev.map((item) =>
          item.id === row.id
            ? { ...item, attribute, description, status }
            : item
        )
      );
    } else {
      const relationshipRow = row as RelationshipRow;
      const sourceNode = modifyForm.sourceNode ?? relationshipRow.sourceNode;
      const relationshipType =
        modifyForm.relationshipType ?? relationshipRow.relationshipType;
      const targetNode = modifyForm.targetNode ?? relationshipRow.targetNode;

      if (initiativeId) {
        const sourceNodeId =
          conceptRows.find((concept) => concept.nodeName === sourceNode)?.id ??
          sourceNode;
        const targetNodeId =
          conceptRows.find((concept) => concept.nodeName === targetNode)?.id ??
          targetNode;
        try {
          await OntologyService.updateEntity(
            initiativeId,
            "relationship",
            row.id,
            {
              sourceNodeId,
              relationshipName: relationshipType,
              targetNodeId
            }
          );
        } catch {
          setIsSavingModify(false);
          return;
        }
      }

      setRelationshipRows((prev) =>
        prev.map((item) =>
          item.id === row.id
            ? { ...item, sourceNode, relationshipType, targetNode }
            : item
        )
      );
    }

    setIsSavingModify(false);
    closeModifyDrawer();
  };

  // Fetches the generated Concepts/Attributes/Relationships for this initiative from the
  // real backend (GET /initiatives/{initiativeId}/entities-relationships), optionally filtered.
  // Replaces all currently-loaded rows with page 1 of each list (used for the initial load
  // and whenever filters change).
  const fetchEntitiesRelationships = (
    filters?: EntitiesRelationshipsQueryFilters
  ) => {
    if (!initiativeId) return;
    setIsLoading(true);
    OntologyService.getEntitiesRelationships(initiativeId, filters)
      .then((data) => {
        setConceptRows(data.conceptRows);
        setAttributeRows(data.attributeRows);
        setRelationshipRows(data.relationshipRows);
        setSummary(data.summary);
        setConceptsPagination({
          ...data.pagination.nodes,
          isLoadingMore: false
        });
        setAttributesPagination({
          ...data.pagination.attributes,
          isLoadingMore: false
        });
        setRelationshipsPagination({
          ...data.pagination.relationships,
          isLoadingMore: false
        });
        setHasLoadedConcepts(true);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (!initiativeId) return;
    let isMounted = true;
    (async () => {
      const initiative = await OntologyService.getInitiativeById(initiativeId);
      if (!isMounted) return;
      if (initiative.currentStageName === ENTITY_GENERATION_FAILED_STAGE) {
        setEntitiesGenerationFailed(true);
        return;
      }
      if (!ENTITIES_READY_STAGES.includes(initiative.currentStageName)) {
        setIsGeneratingEntities(true);
        const finalStage = await waitForEntitiesGeneration(
          initiativeId,
          () => isMounted
        );
        if (!isMounted) return;
        if (finalStage === ENTITY_GENERATION_FAILED_STAGE) {
          setIsGeneratingEntities(false);
          setEntitiesGenerationFailed(true);
          return;
        }
        if (!finalStage || !ENTITIES_READY_STAGES.includes(finalStage)) {
          // Poll timed out and generation is still in progress — keep the loader showing.
          return;
        }
        setIsGeneratingEntities(false);
      }
      fetchEntitiesRelationships();
      // Lookup data for the "Business Question"/"Competency Question" cascading selects;
      // only fetched once entities generation has finished polling.
      OntologyService.getOntologyBusinessQuestions(initiativeId).then(
        setBusinessQuestions
      );
      OntologyService.getCompetencyQuestions(initiativeId, 1, 10000).then(
        (page) => setCompetencyQuestions(page.rows)
      );
    })();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiativeId]);

  // Fetches the Concepts/Relationships graph for this initiative (GET .../graph-view).
  const fetchGraphView = () => {
    if (!initiativeId) return;
    setIsLoadingGraph(true);
    OntologyService.getGraphView(initiativeId)
      .then((data) => {
        setGraphNodes(data.nodes);
        setGraphEdges(data.edges);
      })
      .finally(() => setIsLoadingGraph(false));
  };

  // Toggles selection on/off so re-clicking an already-selected node deselects it
  // (returning it to its normal 1x size) instead of re-selecting it every time.
  const toggleGraphNodeSelection = (nodeId: string) => {
    setSelectedGraphNodeId((prev) => (prev === nodeId ? null : nodeId));
  };

  const closeRelatedEntities = relatedEntities.close;

  // Returns the Graph Directory panel to the Nodes list from either the Related Entities
  // view or the single-click Node Details view.
  const backToGraphDirectoryList = () => {
    closeRelatedEntities();
    setSelectedGraphNodeId(null);
  };

  // Loads the selected node's detail + BQ/CQ traceability into the Graph Directory panel's
  // "Node Details" tab instead of opening the old View drawer; clearing selection reverts
  // the panel to the "Nodes" list tab.
  useEffect(() => {
    if (!selectedGraphNodeId || !initiativeId) {
      setSelectedNodeDetail(null);
      setSelectedNodeAttributes([]);
      setGraphDirectoryTab("nodes");
      return;
    }
    let isCancelled = false;
    setGraphDirectoryTab("details");
    setIsLoadingNodeDetail(true);
    setSelectedNodeAttributes([]);
    OntologyService.getNodeDetail(initiativeId, selectedGraphNodeId)
      .then((detail) => {
        if (!isCancelled) setSelectedNodeDetail(detail);
      })
      .finally(() => {
        if (!isCancelled) setIsLoadingNodeDetail(false);
      });
    OntologyService.getNodeAttributes(initiativeId, selectedGraphNodeId).then(
      (attributes) => {
        if (!isCancelled) setSelectedNodeAttributes(attributes);
      }
    );
    return () => {
      isCancelled = true;
    };
  }, [selectedGraphNodeId, initiativeId]);

  // Nodes shown in the Graph Directory panel, filtered by the search box.
  const graphDirectoryNodes = useMemo(() => {
    const query = graphDirectorySearch.trim().toLowerCase();
    if (!query) return graphNodes;
    return graphNodes.filter((node) =>
      node.label.toLowerCase().includes(query)
    );
  }, [graphNodes, graphDirectorySearch]);

  // Query filters currently applied (not the popup's staged draft values), reused by the
  // infinite-scroll loadMore* calls so later pages honor the same filters as page 1.
  const buildAppliedFilters = (): EntitiesRelationshipsQueryFilters => ({
    businessQuestionId: businessQuestionIdFilters,
    cqId: competencyQuestionIdFilters,
    status: statusFilters.map((status) => STATUS_QUERY_VALUE[status]),
    nodeType: nodeTypeFilters
  });

  // Fetches the next page of Concepts from the API and appends it to the already-loaded rows.
  const loadMoreConcepts = async () => {
    if (!initiativeId) return;
    if (conceptsPagination.isLoadingMore) return;
    if (conceptsPagination.page >= conceptsPagination.totalPages) return;
    setConceptsPagination((prev) => ({ ...prev, isLoadingMore: true }));
    const result = await OntologyService.getMoreNodes(
      initiativeId,
      conceptsPagination.page + 1,
      buildAppliedFilters()
    );
    setConceptRows((prev) => [...prev, ...result.conceptRows]);
    setConceptsPagination({
      page: result.page,
      totalPages: result.totalPages,
      totalRecords: result.totalRecords,
      isLoadingMore: false
    });
  };

  // Fetches the next page of Attributes; nodeNameById is rebuilt from the Concepts loaded
  // so far so newly-appended attribute rows can resolve their owning node's display name.
  const loadMoreAttributes = async () => {
    if (!initiativeId) return;
    if (attributesPagination.isLoadingMore) return;
    if (attributesPagination.page >= attributesPagination.totalPages) return;
    setAttributesPagination((prev) => ({ ...prev, isLoadingMore: true }));
    const nodeNameById = new Map(
      conceptRows.map((conceptRow) => [conceptRow.id, conceptRow.nodeName])
    );
    const result = await OntologyService.getMoreAttributes(
      initiativeId,
      attributesPagination.page + 1,
      buildAppliedFilters(),
      nodeNameById
    );
    setAttributeRows((prev) => [...prev, ...result.attributeRows]);
    setAttributesPagination({
      page: result.page,
      totalPages: result.totalPages,
      totalRecords: result.totalRecords,
      isLoadingMore: false
    });
  };

  // Fetches the next page of Relationships; nodeNameById resolves source/target node names.
  const loadMoreRelationships = async () => {
    if (!initiativeId) return;
    if (relationshipsPagination.isLoadingMore) return;
    if (relationshipsPagination.page >= relationshipsPagination.totalPages)
      return;
    setRelationshipsPagination((prev) => ({ ...prev, isLoadingMore: true }));
    const nodeNameById = new Map(
      conceptRows.map((conceptRow) => [conceptRow.id, conceptRow.nodeName])
    );
    const result = await OntologyService.getMoreRelationships(
      initiativeId,
      relationshipsPagination.page + 1,
      buildAppliedFilters(),
      nodeNameById
    );
    setRelationshipRows((prev) => [...prev, ...result.relationshipRows]);
    setRelationshipsPagination({
      page: result.page,
      totalPages: result.totalPages,
      totalRecords: result.totalRecords,
      isLoadingMore: false
    });
  };

  // Fetches the next page once the user scrolls near the bottom of the table.
  const handleTableScroll =
    (tab: ContentTab) => (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      if (scrollHeight - scrollTop - clientHeight >= 80) return;
      if (tab === "concepts") loadMoreConcepts();
      else if (tab === "attributes") loadMoreAttributes();
      else loadMoreRelationships();
    };

  const rowsForActiveTab: { id: string }[] =
    activeTab === "concepts"
      ? conceptRows
      : activeTab === "attributes"
      ? attributeRows
      : relationshipRows;

  const summaryForActiveTab =
    activeTab === "concepts"
      ? summary.nodes
      : activeTab === "attributes"
      ? summary.attributes
      : summary.relationships;

  const selectedIdsForActiveTab =
    activeTab === "concepts"
      ? conceptSelectedIds
      : activeTab === "attributes"
      ? attributeSelectedIds
      : relationshipSelectedIds;

  const allSelected =
    rowsForActiveTab.length > 0 &&
    selectedIdsForActiveTab.length === rowsForActiveTab.length;

  const setSelectedIdsForActiveTab = (
    next: string[] | ((prev: string[]) => string[])
  ) => {
    if (activeTab === "concepts") setConceptSelectedIds(next);
    else if (activeTab === "attributes") setAttributeSelectedIds(next);
    else setRelationshipSelectedIds(next);
  };

  const toggleSelectAll = () => {
    setSelectedIdsForActiveTab(
      allSelected ? [] : rowsForActiveTab.map((row) => row.id)
    );
  };

  const toggleSelectRow = (id: string) => {
    setSelectedIdsForActiveTab((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const applyBulkStatus = (status: ConceptStatus) => {
    const idsToUpdate = selectedIdsForActiveTab;
    const entityType =
      activeTab === "concepts"
        ? "nodes"
        : activeTab === "attributes"
        ? "attributes"
        : "relationships";
    // Rows actually changing status, read before the state update, so the summary counts
    // below can be adjusted without a full refetch.
    const sourceRows: { id: string; status: ConceptStatus }[] =
      activeTab === "concepts"
        ? conceptRows
        : activeTab === "attributes"
        ? attributeRows
        : relationshipRows;
    const changedRows = sourceRows.filter(
      (row) => idsToUpdate.includes(row.id) && row.status !== status
    );

    if (activeTab === "concepts") {
      setConceptRows((prev) =>
        prev.map((row) =>
          idsToUpdate.includes(row.id) ? { ...row, status } : row
        )
      );
    } else if (activeTab === "attributes") {
      setAttributeRows((prev) =>
        prev.map((row) =>
          idsToUpdate.includes(row.id) ? { ...row, status } : row
        )
      );
    } else {
      setRelationshipRows((prev) =>
        prev.map((row) =>
          idsToUpdate.includes(row.id) ? { ...row, status } : row
        )
      );
    }
    setSelectedIdsForActiveTab([]);

    if (changedRows.length > 0) {
      setSummary((prev) => {
        const bucket = { ...prev[entityType] };
        changedRows.forEach((row) => {
          bucket[STATUS_SUMMARY_KEY[row.status]] -= 1;
        });
        bucket[STATUS_SUMMARY_KEY[status]] += changedRows.length;
        return { ...prev, [entityType]: bucket };
      });
    }

    if (!initiativeId || idsToUpdate.length === 0) return;
    const action = status === "Accepted" ? "accept" : "reject";
    OntologyService.updateEntitiesStatus(
      initiativeId,
      entityType,
      action,
      idsToUpdate
    );
  };

  // Prepends a newly created entity to whichever table matches its type.
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const [addItemType, setAddItemType] = useState<ContentTab>("concepts");
  const [addForm, setAddForm] = useState<AddFormState>({});
  const [isCreatingEntity, setIsCreatingEntity] = useState(false);
  const [addEntityError, setAddEntityError] = useState<string | null>(null);
  const [businessQuestions, setBusinessQuestions] = useState<
    OntologyBusinessQuestionItem[]
  >([]);
  const [competencyQuestions, setCompetencyQuestions] = useState<
    CompetencyQuestionRow[]
  >([]);

  const getCompetencyQuestionsForBqs = (
    businessQuestionIds: string[] | undefined
  ): CompetencyQuestionRow[] =>
    businessQuestionIds?.length
      ? competencyQuestions.filter((cq) =>
          businessQuestionIds.includes(cq.businessQuestionId)
        )
      : [];

  // Applied vs. draft (staged, popup-only) Business Question/Competency Question/Status/Type filters.
  const [businessQuestionIdFilters, setBusinessQuestionIdFilters] = useState<
    string[]
  >([]);
  const [competencyQuestionIdFilters, setCompetencyQuestionIdFilters] =
    useState<string[]>([]);
  const [statusFilters, setStatusFilters] = useState<ConceptStatus[]>([]);
  const [nodeTypeFilters, setNodeTypeFilters] = useState<string[]>([]);
  const [draftBusinessQuestionIdFilters, setDraftBusinessQuestionIdFilters] =
    useState<string[]>([]);
  const [
    draftCompetencyQuestionIdFilters,
    setDraftCompetencyQuestionIdFilters
  ] = useState<string[]>([]);
  const [draftStatusFilters, setDraftStatusFilters] = useState<ConceptStatus[]>(
    []
  );
  const [draftNodeTypeFilters, setDraftNodeTypeFilters] = useState<string[]>(
    []
  );
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const filterPopupRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(filterPopupRef, () => setShowFilterPopup(false));

  const hasExtraFilters =
    businessQuestionIdFilters.length > 0 ||
    competencyQuestionIdFilters.length > 0 ||
    statusFilters.length > 0 ||
    nodeTypeFilters.length > 0;

  const extraFiltersCount =
    businessQuestionIdFilters.length +
    competencyQuestionIdFilters.length +
    statusFilters.length +
    nodeTypeFilters.length;

  // Discards any unsaved popup changes and reloads it from the currently applied filters.
  const openFilterPopup = () => {
    setDraftBusinessQuestionIdFilters(businessQuestionIdFilters);
    setDraftCompetencyQuestionIdFilters(competencyQuestionIdFilters);
    setDraftStatusFilters(statusFilters);
    setDraftNodeTypeFilters(nodeTypeFilters);
    setShowFilterPopup(true);
  };

  const toggleBusinessQuestionFilter = (businessQuestionId: string) => {
    setDraftBusinessQuestionIdFilters((prev) =>
      prev.includes(businessQuestionId)
        ? prev.filter((item) => item !== businessQuestionId)
        : [...prev, businessQuestionId]
    );
  };

  const toggleCompetencyQuestionFilter = (cqId: string) => {
    setDraftCompetencyQuestionIdFilters((prev) =>
      prev.includes(cqId)
        ? prev.filter((item) => item !== cqId)
        : [...prev, cqId]
    );
  };

  const toggleStatusFilter = (status: ConceptStatus) => {
    setDraftStatusFilters((prev) =>
      prev.includes(status)
        ? prev.filter((item) => item !== status)
        : [...prev, status]
    );
  };

  const toggleNodeTypeFilter = (value: string) => {
    setDraftNodeTypeFilters((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value]
    );
  };

  const clearExtraFilters = () => {
    setDraftBusinessQuestionIdFilters([]);
    setDraftCompetencyQuestionIdFilters([]);
    setDraftStatusFilters([]);
    setDraftNodeTypeFilters([]);
  };

  // Commits the staged selections by re-fetching from the backend with them applied as
  // query params (businessQuestionId/cqId/status/nodeType), rather than filtering client-side.
  const applyExtraFilters = async () => {
    setIsApplyingFilters(true);
    try {
      fetchEntitiesRelationships({
        businessQuestionId: draftBusinessQuestionIdFilters,
        cqId: draftCompetencyQuestionIdFilters,
        status: draftStatusFilters.map((status) => STATUS_QUERY_VALUE[status]),
        nodeType: draftNodeTypeFilters
      });
      setBusinessQuestionIdFilters(draftBusinessQuestionIdFilters);
      setCompetencyQuestionIdFilters(draftCompetencyQuestionIdFilters);
      setStatusFilters(draftStatusFilters);
      setNodeTypeFilters(draftNodeTypeFilters);
      setShowFilterPopup(false);
    } finally {
      setIsApplyingFilters(false);
    }
  };

  // Opens the Add drawer for whichever tab is currently active (Concepts/Attributes/Relationships).
  const openAddDrawer = () => {
    setAddItemType(activeTab);
    setAddForm({});
    setAddEntityError(null);
    setIsAddDrawerOpen(true);
  };

  const closeAddDrawer = () => {
    setIsAddDrawerOpen(false);
    setAddForm({});
    setAddEntityError(null);
  };

  const updateAddForm = <K extends keyof AddFormState>(
    key: K,
    value: AddFormState[K]
  ) => {
    setAddForm((prev) => ({ ...prev, [key]: value }));
  };

  // Creates a new node/attribute/relationship via POST /initiatives/{initiativeId}/entities,
  // then prepends the created row (using the backend-assigned id) to the matching table.
  const saveAddDrawer = async () => {
    if (!initiativeId) return;
    const businessQuestionIds = addForm.businessQuestionIds ?? [];
    const cqIds = addForm.competencyQuestionIds ?? [];
    if (businessQuestionIds.length === 0 || cqIds.length === 0) {
      setAddEntityError(
        "Select at least one Business Question and one Competency Question."
      );
      return;
    }
    const cqCodes = competencyQuestions
      .filter((cq) => cqIds.includes(cq.id))
      .map((cq) => cq.cqId);
    const bqCodes = businessQuestions
      .filter((bq) => businessQuestionIds.includes(bq.businessQuestionId))
      .map((bq) => bq.bqCode);
    const cqId = cqIds[0];
    const cqCode = cqCodes[0] ?? "-";
    const bqCode = bqCodes[0] ?? "-";

    setAddEntityError(null);
    setIsCreatingEntity(true);
    try {
      if (addItemType === "concepts") {
        const nodeType = addForm.nodeType?.trim() || "Class";
        const response = await OntologyService.createEntity(initiativeId, {
          entityType: "node",
          businessQuestionIds,
          cqIds,
          nodeName: addForm.nodeName ?? "",
          nodeType,
          description: addForm.description ?? "",
          status: "Draft"
        });
        setConceptRows((prev) => [
          {
            id: response.entityId,
            cqId,
            cqCode,
            cqCodes,
            bqCode,
            bqCodes,
            nodeName: addForm.nodeName ?? "",
            nodeType: nodeType.toUpperCase().includes("DOC")
              ? "Document"
              : "Class",
            nodeTypeRaw: nodeType,
            description: addForm.description ?? "",
            confidenceScore: 100,
            status: "Draft"
          },
          ...prev
        ]);
      } else if (addItemType === "attributes") {
        const owningNode = conceptRows.find(
          (concept) => concept.id === addForm.nodeId
        );
        const response = await OntologyService.createEntity(initiativeId, {
          entityType: "attribute",
          businessQuestionIds,
          cqIds,
          nodeId: addForm.nodeId ?? "",
          attributeName: addForm.attribute ?? "",
          description: addForm.description ?? "",
          status: "Draft"
        });
        setAttributeRows((prev) => [
          {
            id: response.entityId,
            cqId,
            cqCode,
            cqCodes,
            bqCode,
            bqCodes,
            nodeName: owningNode?.nodeName ?? "",
            attribute: addForm.attribute ?? "",
            description: addForm.description ?? "",
            confidenceScore: null,
            status: "Draft"
          },
          ...prev
        ]);
      } else {
        const sourceNodeId =
          conceptRows.find((concept) => concept.nodeName === addForm.sourceNode)
            ?.id ?? "";
        const targetNodeId =
          conceptRows.find((concept) => concept.nodeName === addForm.targetNode)
            ?.id ?? "";
        const response = await OntologyService.createEntity(initiativeId, {
          entityType: "relationship",
          businessQuestionIds,
          cqIds,
          sourceNodeId,
          targetNodeId,
          relationshipName: addForm.relationshipType ?? "",
          description: addForm.description ?? "",
          status: "Draft"
        });
        setRelationshipRows((prev) => [
          {
            id: response.entityId,
            cqId,
            cqCode,
            cqCodes,
            bqCode,
            bqCodes,
            sourceNode: addForm.sourceNode ?? "",
            relationshipType: addForm.relationshipType ?? "",
            targetNode: addForm.targetNode ?? "",
            description: addForm.description ?? "",
            confidenceScore: null,
            status: "Draft"
          },
          ...prev
        ]);
      }
      closeAddDrawer();
    } catch {
      setAddEntityError("Failed to create entity. Please try again.");
    } finally {
      setIsCreatingEntity(false);
    }
  };

  // Exports the current Concepts/Attributes/Relationships tables as a 3-tab Excel workbook.
  const handleExport = () => {
    const conceptSheet = XLSX.utils.json_to_sheet(
      conceptRows.map((row) => ({
        "CQ ID": row.cqCode,
        "Node Name": row.nodeName,
        "Node Type": row.nodeType,
        Description: row.description,
        Status: row.status
      }))
    );
    const attributeSheet = XLSX.utils.json_to_sheet(
      attributeRows.map((row) => ({
        "CQ ID": row.cqCode,
        "Node Name": row.nodeName,
        "Node Type": row.attribute,
        Description: row.description,
        Status: row.status
      }))
    );
    const relationshipSheet = XLSX.utils.json_to_sheet(
      relationshipRows.map((row) => ({
        "CQ ID": row.cqCode,
        "Node Name": row.sourceNode,
        "Node Type": row.relationshipType,
        "Target Node": row.targetNode,
        Description: row.description,
        Status: row.status
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, conceptSheet, "Concepts");
    XLSX.utils.book_append_sheet(workbook, attributeSheet, "Attributes");
    XLSX.utils.book_append_sheet(workbook, relationshipSheet, "Relationships");
    XLSX.writeFile(
      workbook,
      `entities-relationships-${initiativeId ?? "export"}.xlsx`
    );
  };

  const handleBack = () => {
    navigate("/ontology-studio/create/questions", { state: { initiativeId } });
  };

  // Queues reuse/harmonize analysis, then redirects immediately — the destination page polls
  // for the analysis to finish rather than blocking navigation here.
  const handleApproveContinue = async () => {
    if (!initiativeId) return;
    setHarmonizationError(null);
    setIsTriggeringHarmonization(true);
    try {
      await OntologyService.triggerReuseHarmonizeAnalysis(initiativeId);
      navigate("/ontology-studio/create/reuse-harmonisation", {
        state: { initiativeId, harmonizationTriggered: true }
      });
    } catch {
      if (isMountedRef.current) {
        setHarmonizationError(
          "Failed to start Reuse & Harmonisation analysis."
        );
      }
    } finally {
      if (isMountedRef.current) setIsTriggeringHarmonization(false);
    }
  };

  const tabLabels: { id: ContentTab; label: string }[] = useMemo(
    () => [
      { id: "concepts", label: "Concepts" },
      { id: "attributes", label: "Attributes" },
      { id: "relationships", label: "Relationships" }
    ],
    []
  );

  const getEntityTypeLabel = (type: ContentTab): string =>
    type === "concepts"
      ? "Concept"
      : type === "attributes"
      ? "Attribute"
      : "Relationship";

  // Mirrors Figma's per-type subtitle, e.g. "View operatingPressure for Equipment."
  const getViewDrawerSubtitle = (
    type: ContentTab,
    row: ConceptRow | AttributeRow | RelationshipRow
  ): string => {
    if (type === "concepts") {
      return `View ${(row as ConceptRow).nodeName}.`;
    }
    if (type === "attributes") {
      const attributeRow = row as AttributeRow;
      return `View ${attributeRow.attribute} for ${attributeRow.nodeName}.`;
    }
    const relationshipRow = row as RelationshipRow;
    return `View ${relationshipRow.sourceNode} → ${relationshipRow.relationshipType} → ${relationshipRow.targetNode}.`;
  };

  // Mirrors Figma's Modify drawer subtitles per type.
  const getModifyDrawerSubtitle = (
    type: ContentTab,
    row: ConceptRow | AttributeRow | RelationshipRow
  ): string => {
    if (type === "concepts") {
      return "Update ontology entity details and relationships.";
    }
    if (type === "attributes") {
      const attributeRow = row as AttributeRow;
      return `Update ${attributeRow.attribute} for ${attributeRow.nodeName}.`;
    }
    const relationshipRow = row as RelationshipRow;
    return `Update ${relationshipRow.sourceNode} → ${relationshipRow.relationshipType} → ${relationshipRow.targetNode}.`;
  };

  // Mirrors Figma's Add drawer title/subtitle per type.
  const getAddDrawerTitle = (type: ContentTab): string =>
    type === "concepts"
      ? "Add New Concept"
      : type === "attributes"
      ? "Add Attribute"
      : "Add Relationship";

  const getAddDrawerSubtitle = (type: ContentTab): string =>
    type === "concepts"
      ? "Create a new ontology entity linked to a competency question."
      : type === "attributes"
      ? "Create an attribute linked to an ontology node and competency question."
      : "Create a relationship between ontology nodes linked to a competency question.";

  // Total concepts + attributes + relationships for this initiative (from the summary, which
  // covers every record, not just the ones currently loaded into the paginated tables).
  const totalEntitiesCount =
    summary.nodes.total +
    summary.attributes.total +
    summary.relationships.total;
  // Still-undecided items (Draft or Needs Review) across all three types; Approve & Continue
  // stays disabled until this reaches zero.
  const pendingDecisionCount =
    summary.nodes.draft +
    summary.nodes.needsReview +
    summary.attributes.draft +
    summary.attributes.needsReview +
    summary.relationships.draft +
    summary.relationships.needsReview;

  return (
    <div className="create-entities-container">
      <nav className="create-entities-breadcrumb">
        <Link to="/home" className="breadcrumb-link">
          Engineering Information Management
        </Link>
        <span className="breadcrumb-separator">&gt;</span>
        <Link to="/create-ontology" className="breadcrumb-link">
          Ontology Studio
        </Link>
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">Entities &amp; Relationships</span>
      </nav>

      <div className="create-entities-header">
        <h1 className="create-entities-title">Entities &amp; Relationships</h1>
        <p className="create-entities-description">
          Review Concepts and Relationships generated from competency questions
          before continuing to reuse, grounding, and release.
        </p>
        <div className="create-entities-meta">
          <span className="create-entities-meta__pill">
            Initiative ID <strong>{initiativeId ?? "EY-ONT-2026-TEMP"}</strong>
          </span>
          <span className="create-entities-meta__pill">
            Status <strong>Draft</strong>
          </span>
        </div>
      </div>

      <div className="create-entities-workflow">
        {ONTOLOGY_WORKBENCH_STEPS.map((step) => (
          <div
            key={step.id}
            className={`create-entities-workflow__step${
              step.id === 3 ? " create-entities-workflow__step--active" : ""
            }${step.id < 3 ? " create-entities-workflow__step--done" : ""}`}
          >
            <span className="create-entities-workflow__indicator">
              {step.id < 3 ? "✓" : step.id === 3 ? "•" : ""}
            </span>
            <span className="create-entities-workflow__label">
              {step.label}
            </span>
          </div>
        ))}
      </div>

      {entitiesGenerationFailed ? (
        <div className="create-entities-table-wrapper create-entities-table-wrapper--empty create-entities-processing create-entities-processing--error">
          <ErrorOutline className="create-entities-processing__icon" />
          <p className="create-entities-processing__title">
            Entity Generation Failed
          </p>
          <p className="create-entities-processing__text">
            We were unable to generate entities and relationships for this
            initiative. Please return to the Competency Questions screen to
            review your questions and regenerate.
          </p>
          <Link
            to="/ontology-studio/create/questions"
            state={{ initiativeId }}
            className="create-entities-processing__link-btn"
          >
            Back to Competency Questions
          </Link>
        </div>
      ) : isGeneratingEntities ? (
        showEntitiesGenerationShimmer ? (
          <EntitiesGenerationShimmer />
        ) : (
          <div className="create-entities-table-wrapper create-entities-table-wrapper--empty">
            <Spinner size={24} />
          </div>
        )
      ) : (
        <>
          <div className="create-entities-tabs-row">
            <div className="create-entities-tabs">
              {tabLabels.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`create-entities-tabs__item${
                    activeTab === tab.id
                      ? " create-entities-tabs__item--active"
                      : ""
                  }`}
                  disabled={viewMode === "graph"}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="create-entities-tabs">
              <button
                type="button"
                className={`create-entities-tabs__item${
                  viewMode === "table"
                    ? " create-entities-tabs__item--active"
                    : ""
                }`}
                onClick={() => setViewMode("table")}
              >
                Table View
              </button>
              <button
                type="button"
                className={`create-entities-tabs__item${
                  viewMode === "graph"
                    ? " create-entities-tabs__item--active"
                    : ""
                }`}
                onClick={() => {
                  setViewMode("graph");
                  fetchGraphView();
                }}
              >
                Graph View
              </button>
            </div>
          </div>

          {(viewMode === "table" || viewDrawerEntity || modifyDrawerEntity) &&
            hasLoadedConcepts && (
              <div className="create-entities-summary-row">
                <div className="create-entities-summary-card">
                  <span className="create-entities-summary-card__label">
                    {tabLabels.find((tab) => tab.id === activeTab)?.label}
                  </span>
                  <span className="create-entities-summary-card__value">
                    {summaryForActiveTab.total}
                  </span>
                </div>
                <div className="create-entities-summary-card create-entities-summary-card--draft">
                  <span className="create-entities-summary-card__label">
                    Draft
                  </span>
                  <span className="create-entities-summary-card__value">
                    {summaryForActiveTab.draft}
                  </span>
                </div>
                <div className="create-entities-summary-card create-entities-summary-card--approved">
                  <span className="create-entities-summary-card__label">
                    Approved
                  </span>
                  <span className="create-entities-summary-card__value">
                    {summaryForActiveTab.approved}
                  </span>
                </div>
                <div className="create-entities-summary-card create-entities-summary-card--needs-review">
                  <span className="create-entities-summary-card__label">
                    Needs Review
                  </span>
                  <span className="create-entities-summary-card__value">
                    {summaryForActiveTab.needsReview}
                  </span>
                </div>
                <div className="create-entities-summary-card create-entities-summary-card--rejected">
                  <span className="create-entities-summary-card__label">
                    Rejected
                  </span>
                  <span className="create-entities-summary-card__value">
                    {summaryForActiveTab.rejected}
                  </span>
                </div>
              </div>
            )}

          {viewMode === "graph" && (
            <div className="create-entities-graph-wrapper">
              <div className="create-entities-graph-wrapper__header">
                <h3 className="create-entities-graph-wrapper__title">
                  Knowledge Graph Visualization
                </h3>
              </div>
              <p className="create-entities-graph-wrapper__subtitle">
                Visual exploration of the entities and relationships.
              </p>
              {isLoadingGraph ? (
                <div className="create-entities-table-wrapper create-entities-table-wrapper--empty">
                  <Spinner size={20} />
                </div>
              ) : graphNodes.length > 0 ? (
                <div className="create-entities-graph-and-inspector">
                  <div
                    className={`create-entities-graph${
                      relatedEntities.isInitialLoading
                        ? " create-entities-graph--loading"
                        : ""
                    }`}
                  >
                    <GraphCanvasOntology
                      nodes={graphNodes}
                      edges={graphEdges}
                      selectedNodeId={selectedGraphNodeId}
                      onNodeSelect={toggleGraphNodeSelection}
                      onNodeDoubleClick={relatedEntities.open}
                      onNodeView={(nodeId) => {
                        const row = conceptRows.find(
                          (concept) => concept.id === nodeId
                        );
                        if (row) openViewDrawer("concepts", row);
                      }}
                      onNodeModify={(nodeId) => {
                        const row = conceptRows.find(
                          (concept) => concept.id === nodeId
                        );
                        if (row) openModifyDrawer("concepts", row);
                      }}
                      getNodeStatus={(nodeId) =>
                        conceptRows.find((concept) => concept.id === nodeId)
                          ?.status
                      }
                      scrollZoom
                    />
                    {relatedEntities.isInitialLoading && (
                      <div className="create-entities-graph__loading-overlay">
                        <Spinner size={24} />
                      </div>
                    )}
                    {relatedEntities.isOpen && relatedEntities.subGraph && (
                      <RelatedEntitiesGraphOverlay
                        subGraph={relatedEntities.subGraph}
                        focusedNodeId={
                          relatedEntities.focusedNode?.id ??
                          relatedEntities.nodeId
                        }
                        focusedNodeLabel={
                          relatedEntities.focusedNode?.label ?? null
                        }
                        focusedNodeType={
                          relatedEntities.focusedNode?.nodeType ?? null
                        }
                        connectedCount={relatedEntities.connectedNodes.length}
                        isDrilling={relatedEntities.isLoading}
                        selectedNodeId={relatedEntities.selectedNodeId}
                        onNodeDoubleClick={relatedEntities.open}
                        onNodeSelect={relatedEntities.selectNode}
                        onClose={relatedEntities.close}
                      />
                    )}
                  </div>
                  <div className="create-entities-graph-directory">
                    <div className="create-entities-graph-directory__header">
                      <p className="create-entities-graph-directory__title">
                        Graph Directory
                      </p>
                      {(relatedEntities.isOpen ||
                        graphDirectoryTab === "details") && (
                        <button
                          type="button"
                          className="create-entities-graph-directory__back-btn"
                          onClick={backToGraphDirectoryList}
                        >
                          <ArrowBackOutlined fontSize="small" />
                          Back to list
                        </button>
                      )}
                    </div>
                    <div className="create-entities-graph-directory__body">
                      {relatedEntities.isOpen ? (
                        <RelatedEntitiesPanel
                          isInitialLoading={relatedEntities.isInitialLoading}
                          hasData={relatedEntities.data !== null}
                          focusedNode={relatedEntities.focusedNode}
                          connectedNodes={relatedEntities.connectedNodes}
                          filteredConnectedNodes={
                            relatedEntities.filteredConnectedNodes
                          }
                          search={relatedEntities.search}
                          onSearchChange={relatedEntities.setSearch}
                          onClose={relatedEntities.close}
                          selectedNodeId={relatedEntities.selectedNodeId}
                          selectedNodeDetail={
                            relatedEntities.selectedNodeDetail
                          }
                          selectedNodeAttributes={
                            relatedEntities.selectedNodeAttributes
                          }
                          isLoadingSelectedNodeDetail={
                            relatedEntities.isLoadingSelectedNodeDetail
                          }
                          onSelectNode={relatedEntities.selectNode}
                          onBackToConnectedList={
                            relatedEntities.clearSelectedNode
                          }
                        />
                      ) : graphDirectoryTab === "nodes" ? (
                        <>
                          <div className="create-entities-graph-directory__title-row">
                            <span>Nodes ({graphNodes.length})</span>
                            <span>{graphDirectoryNodes.length} visible</span>
                          </div>
                          <div className="create-entities-graph-directory__search">
                            <SearchOutlined fontSize="small" />
                            <input
                              type="text"
                              placeholder="Filter nodes"
                              value={graphDirectorySearch}
                              onChange={(event) =>
                                setGraphDirectorySearch(event.target.value)
                              }
                            />
                          </div>
                          <div className="create-entities-graph-directory__list">
                            {graphDirectoryNodes.map((node) => {
                              const isSelected =
                                selectedGraphNodeId === node.id;
                              return (
                                <button
                                  type="button"
                                  key={node.id}
                                  className={`create-entities-graph-directory__row${
                                    isSelected
                                      ? " create-entities-graph-directory__row--selected"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    toggleGraphNodeSelection(node.id)
                                  }
                                >
                                  <span
                                    className="create-entities-graph-directory__row-swatch"
                                    style={{
                                      backgroundColor: getNodeColor(node.type)
                                    }}
                                  />
                                  <span
                                    className="create-entities-graph-directory__row-name"
                                    title={node.label}
                                  >
                                    {node.label}
                                  </span>
                                  <span
                                    className={`create-entities-graph-directory__row-badge${
                                      isSelected
                                        ? " create-entities-graph-directory__row-badge--selected"
                                        : ""
                                    }`}
                                    title={node.type}
                                  >
                                    {node.type}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </>
                      ) : isLoadingNodeDetail ? (
                        <div className="create-entities-node-detail__loading">
                          <Spinner size={20} />
                        </div>
                      ) : selectedNodeDetail ? (
                        <div className="create-entities-node-detail">
                          <div className="create-entities-node-detail__summary">
                            <div className="create-entities-node-detail__identity">
                              <span
                                className="create-entities-node-detail__swatch"
                                style={{
                                  backgroundColor: getNodeColor(
                                    selectedNodeDetail.conceptRow.nodeTypeRaw
                                  )
                                }}
                              />
                              <span className="create-entities-node-detail__name">
                                {selectedNodeDetail.conceptRow.nodeName}
                              </span>
                              <span className="create-entities-node-detail__type-pill">
                                {selectedNodeDetail.conceptRow.nodeTypeRaw}
                              </span>
                            </div>
                            <p className="create-entities-node-detail__focus">
                              {selectedNodeDetail.conceptRow.description ||
                                "No description available."}
                            </p>
                          </div>

                          <div className="create-entities-node-detail__section">
                            <h4 className="create-entities-node-detail__section-title">
                              Attributes
                            </h4>
                            {selectedNodeAttributes.length === 0 ? (
                              <div className="create-entities-node-detail__attributes-empty">
                                No attribute values are available for this node.
                              </div>
                            ) : (
                              <div className="create-entities-node-detail__attributes-list">
                                {selectedNodeAttributes.map((attribute) => (
                                  <div
                                    key={attribute.id}
                                    className="create-entities-node-detail__attribute"
                                  >
                                    <strong>{attribute.attribute}</strong>
                                    <span>{attribute.description}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="create-entities-node-detail__section">
                            <h4 className="create-entities-node-detail__section-title">
                              Ontology Traceability
                            </h4>
                            <div className="create-entities-node-detail__traceability">
                              <p className="create-entities-node-detail__traceability-label">
                                Reference example
                              </p>
                              <div className="create-entities-node-detail__traceability-group">
                                <h5>Business Question</h5>
                                {selectedNodeDetail.businessQuestions.length ===
                                0 ? (
                                  <p className="create-entities-node-detail__traceability-empty">
                                    Not recorded yet
                                  </p>
                                ) : (
                                  selectedNodeDetail.businessQuestions.map(
                                    (bq) => (
                                      <div
                                        key={bq.businessQuestionId}
                                        className="create-entities-node-detail__bq-card"
                                      >
                                        <span className="create-entities-node-detail__bq-badge">
                                          {bq.bqCode}
                                        </span>
                                        <p>{bq.questionText}</p>
                                      </div>
                                    )
                                  )
                                )}
                              </div>
                              <div className="create-entities-node-detail__traceability-group">
                                <h5>Competency Questions</h5>
                                {selectedNodeDetail.competencyQuestions
                                  .length === 0 ? (
                                  <p className="create-entities-node-detail__traceability-empty">
                                    Not recorded yet
                                  </p>
                                ) : (
                                  selectedNodeDetail.competencyQuestions.map(
                                    (cq) => (
                                      <div
                                        key={cq.cqId}
                                        className="create-entities-node-detail__cq-card"
                                      >
                                        <span className="create-entities-node-detail__cq-badge">
                                          {cq.cqCode}
                                        </span>
                                        <p>{cq.questionText}</p>
                                      </div>
                                    )
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="create-entities-node-detail__attributes-empty">
                          Select a node to view its details.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="create-entities-table-wrapper create-entities-table-wrapper--empty">
                  No graph data available yet.
                </div>
              )}
            </div>
          )}

          {viewMode === "table" && (
            <>
              <div className="create-entities-toolbar">
                <div className="create-entities-toolbar__actions">
                  <button
                    type="button"
                    className="create-entities-btn create-entities-btn--secondary create-entities-btn--disabled"
                    aria-disabled="true"
                  >
                    <UploadOutlined fontSize="small" />
                    <span>Import (Coming Soon)</span>
                  </button>
                  <button
                    type="button"
                    className="create-entities-btn create-entities-btn--secondary"
                    onClick={handleExport}
                  >
                    <DownloadOutlined fontSize="small" />
                    <span>Export</span>
                  </button>
                  <span className="create-entities-toolbar__divider" />
                  <div
                    className="create-entities-filter-popup-anchor"
                    ref={filterPopupRef}
                  >
                    <button
                      type="button"
                      className={`create-entities-btn create-entities-btn--secondary${
                        hasExtraFilters ? " create-entities-btn--active" : ""
                      }`}
                      onClick={() =>
                        showFilterPopup
                          ? setShowFilterPopup(false)
                          : openFilterPopup()
                      }
                    >
                      <TuneOutlined fontSize="small" />
                      <span>Filters</span>
                      {hasExtraFilters && (
                        <span className="create-entities-filter-count">
                          {extraFiltersCount}
                        </span>
                      )}
                    </button>
                    {showFilterPopup && (
                      <div className="create-entities-filter-popup">
                        <div className="create-entities-filter-popup__section">
                          <h4 className="create-entities-filter-popup__title">
                            BQ ID
                          </h4>
                          {businessQuestions.map((bq) => (
                            <label
                              key={bq.businessQuestionId}
                              className="create-entities-filter-popup__checkbox"
                            >
                              <input
                                type="checkbox"
                                checked={draftBusinessQuestionIdFilters.includes(
                                  bq.businessQuestionId
                                )}
                                onChange={() =>
                                  toggleBusinessQuestionFilter(
                                    bq.businessQuestionId
                                  )
                                }
                              />
                              {bq.bqCode}
                            </label>
                          ))}
                        </div>

                        <div className="create-entities-filter-popup__section">
                          <h4 className="create-entities-filter-popup__title">
                            CQ ID
                            {draftCompetencyQuestionIdFilters.length > 0 && (
                              <span className="create-entities-filter-count">
                                {draftCompetencyQuestionIdFilters.length}
                              </span>
                            )}
                          </h4>
                          <div className="create-entities-filter-popup__cq-list">
                            {competencyQuestions.map((cq) => (
                              <label
                                key={cq.id}
                                className="create-entities-filter-popup__checkbox"
                              >
                                <input
                                  type="checkbox"
                                  checked={draftCompetencyQuestionIdFilters.includes(
                                    cq.id
                                  )}
                                  onChange={() =>
                                    toggleCompetencyQuestionFilter(cq.id)
                                  }
                                />
                                {cq.cqId}
                              </label>
                            ))}
                          </div>
                        </div>

                        <div className="create-entities-filter-popup__section">
                          <h4 className="create-entities-filter-popup__title">
                            Status
                          </h4>
                          {STATUS_OPTIONS.map((status) => (
                            <label
                              key={status}
                              className="create-entities-filter-popup__checkbox"
                            >
                              <input
                                type="checkbox"
                                checked={draftStatusFilters.includes(status)}
                                onChange={() => toggleStatusFilter(status)}
                              />
                              {status}
                            </label>
                          ))}
                        </div>

                        <div className="create-entities-filter-popup__footer">
                          <button
                            type="button"
                            className="create-entities-filter-popup__clear"
                            onClick={clearExtraFilters}
                            disabled={
                              draftBusinessQuestionIdFilters.length === 0 &&
                              draftCompetencyQuestionIdFilters.length === 0 &&
                              draftStatusFilters.length === 0 &&
                              draftNodeTypeFilters.length === 0
                            }
                          >
                            Clear all
                          </button>
                          <button
                            type="button"
                            className="create-entities-filter-popup__done"
                            onClick={applyExtraFilters}
                            disabled={isApplyingFilters}
                          >
                            {isApplyingFilters ? "Applying\u2026" : "Done"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="create-entities-btn create-entities-btn--add"
                    onClick={openAddDrawer}
                  >
                    <AddIcon fontSize="small" />
                    <span>Add</span>
                  </button>
                </div>
                <div className="create-entities-toolbar__bulk">
                  <button
                    type="button"
                    className="create-entities-btn create-entities-btn--approve"
                    disabled={selectedIdsForActiveTab.length === 0}
                    onClick={() => applyBulkStatus("Accepted")}
                  >
                    {" "}
                    Approve Selected
                  </button>
                  <button
                    type="button"
                    className="create-entities-btn create-entities-btn--reject"
                    disabled={selectedIdsForActiveTab.length === 0}
                    onClick={() => applyBulkStatus("Rejected")}
                  >
                    Reject Selected
                  </button>
                </div>
              </div>
              {isLoading ? (
                <div className="create-entities-table-wrapper create-entities-table-wrapper--empty">
                  <Spinner size={20} />
                </div>
              ) : (
                <>
                  {activeTab === "concepts" && (
                    <div className="create-entities-table-wrapper">
                      <div className="create-entities-bulk-bar">
                        <button
                          type="button"
                          className={`create-entities-bulk-bar__select${
                            allSelected
                              ? " create-entities-bulk-bar__select--all"
                              : ""
                          }`}
                          aria-label="Select all"
                          onClick={toggleSelectAll}
                        >
                          {allSelected ? (
                            <CheckOutlined fontSize="inherit" />
                          ) : (
                            "-"
                          )}
                        </button>
                        <span className="create-entities-bulk-bar__label">
                          Select all &bull; {selectedIdsForActiveTab.length}{" "}
                          selected out of {conceptsPagination.totalRecords}
                        </span>
                      </div>
                      <div
                        className="create-entities-table-scroll"
                        onScroll={handleTableScroll("concepts")}
                      >
                        <table className="create-entities-table">
                          <thead>
                            <tr>
                              <th className="create-entities-table__col-select"></th>
                              <th className="create-entities-table__col-bq">
                                BQ ID
                              </th>
                              <th className="create-entities-table__col-cq">
                                CQ ID
                              </th>
                              <th className="create-entities-table__col-name">
                                Node Name
                              </th>
                              <th className="create-entities-table__col-name">
                                Node Type
                              </th>
                              <th className="create-entities-table__description">
                                Description
                              </th>
                              <th className="create-entities-table__col-confidence">
                                Confidence Score
                              </th>
                              <th className="create-entities-table__col-status">
                                Status
                              </th>
                              <th className="create-entities-table__col-actions">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {conceptRows.map((row) => (
                              <tr key={row.id}>
                                <td className="create-entities-table__col-select">
                                  <input
                                    type="checkbox"
                                    checked={conceptSelectedIds.includes(
                                      row.id
                                    )}
                                    onChange={() => toggleSelectRow(row.id)}
                                  />
                                </td>
                                <td className="create-entities-table__col-bq">
                                  {renderCodeChips(
                                    row.bqCodes,
                                    `${row.id}-bq`,
                                    BQ_CHIP_VISIBLE_LIMIT
                                  )}
                                </td>
                                <td className="create-entities-table__col-cq">
                                  {renderCodeChips(
                                    row.cqCodes,
                                    `${row.id}-cq`,
                                    CQ_CHIP_VISIBLE_LIMIT
                                  )}
                                </td>
                                <td
                                  className="create-entities-table__col-name"
                                  title={row.nodeName}
                                >
                                  {row.nodeName}
                                </td>
                                <td
                                  className="create-entities-table__col-name"
                                  title={row.nodeTypeRaw}
                                >
                                  {row.nodeTypeRaw}
                                </td>
                                <td
                                  className="create-entities-table__description"
                                  title={row.description}
                                >
                                  {truncateDescription(row.description)}
                                </td>
                                <td className="create-entities-table__col-confidence">
                                  {row.confidenceScore != null
                                    ? `${row.confidenceScore}%`
                                    : "N/A"}
                                </td>
                                <td className="create-entities-table__col-status">
                                  <span
                                    className={`create-entities-status-pill ${
                                      STATUS_CLASS[row.status]
                                    }`}
                                  >
                                    {row.status}
                                  </span>
                                </td>
                                <td className="create-entities-table__col-actions">
                                  <div className="create-entities-row-actions">
                                    <button
                                      type="button"
                                      className="create-entities-action-btn create-entities-action-btn--icon"
                                      aria-label="View"
                                      title="View"
                                      onClick={() =>
                                        openViewDrawer("concepts", row)
                                      }
                                    >
                                      <VisibilityOutlined fontSize="small" />
                                    </button>
                                    <button
                                      type="button"
                                      className="create-entities-action-btn create-entities-action-btn--icon"
                                      aria-label="Modify"
                                      title="Modify"
                                      onClick={() =>
                                        openModifyDrawer("concepts", row)
                                      }
                                    >
                                      <EditOutlined fontSize="small" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="create-entities-table-footer">
                        <span className="create-entities-table-footer__info">
                          Showing {conceptRows.length} of{" "}
                          {conceptsPagination.totalRecords} concepts
                        </span>
                        {conceptsPagination.isLoadingMore ? (
                          <span className="create-entities-table-footer__info">
                            Loading more…
                          </span>
                        ) : (
                          conceptsPagination.page <
                            conceptsPagination.totalPages && (
                            <span className="create-entities-table-footer__info">
                              Scroll down to load more…
                            </span>
                          )
                        )}
                      </div>
                    </div>
                  )}

                  {activeTab === "attributes" && (
                    <div className="create-entities-table-wrapper">
                      <div className="create-entities-bulk-bar">
                        <button
                          type="button"
                          className={`create-entities-bulk-bar__select${
                            allSelected
                              ? " create-entities-bulk-bar__select--all"
                              : ""
                          }`}
                          aria-label="Select all"
                          onClick={toggleSelectAll}
                        >
                          {allSelected ? (
                            <CheckOutlined fontSize="inherit" />
                          ) : (
                            "-"
                          )}
                        </button>
                        <span className="create-entities-bulk-bar__label">
                          Select all &bull; {selectedIdsForActiveTab.length}{" "}
                          selected out of {attributesPagination.totalRecords}
                        </span>
                      </div>
                      <div
                        className="create-entities-table-scroll"
                        onScroll={handleTableScroll("attributes")}
                      >
                        <table className="create-entities-table">
                          <thead>
                            <tr>
                              <th className="create-entities-table__col-select">
                                {" "}
                              </th>
                              <th className="create-entities-table__col-cq">
                                BQ ID
                              </th>
                              <th className="create-entities-table__col-cq">
                                CQ{" "}
                              </th>
                              <th className="create-entities-table__col-name">
                                Node Name
                              </th>
                              <th className="create-entities-table__col-type">
                                Attribute
                              </th>
                              <th className="create-entities-table__description">
                                Description
                              </th>
                              <th className="create-entities-table__col-confidence">
                                Confidence Score
                              </th>
                              <th className="create-entities-table__col-status">
                                Status
                              </th>
                              <th className="create-entities-table__col-actions">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {attributeRows.map((row) => (
                              <tr key={row.id}>
                                <td className="create-entities-table__col-select">
                                  <input
                                    type="checkbox"
                                    checked={attributeSelectedIds.includes(
                                      row.id
                                    )}
                                    onChange={() => toggleSelectRow(row.id)}
                                  />
                                </td>
                                <td className="create-entities-table__col-cq">
                                  {renderCodeChips(
                                    row.bqCodes,
                                    `${row.id}-bq`,
                                    CQ_CHIP_VISIBLE_LIMIT
                                  )}
                                </td>
                                <td className="create-entities-table__col-cq">
                                  {renderCodeChips(
                                    row.cqCodes,
                                    `${row.id}-cq`,
                                    CQ_CHIP_VISIBLE_LIMIT
                                  )}
                                </td>
                                <td className="create-entities-table__col-name">
                                  <span className="create-entities-marquee">
                                    <span className="create-entities-marquee__text">
                                      {row.nodeName}
                                    </span>
                                  </span>
                                </td>
                                <td className="create-entities-table__col-type">
                                  <span className="create-entities-marquee">
                                    <span className="create-entities-marquee__text">
                                      {row.attribute}
                                    </span>
                                  </span>
                                </td>
                                <td
                                  className="create-entities-table__description"
                                  title={row.description}
                                >
                                  {truncateDescription(row.description)}
                                </td>
                                <td className="create-entities-table__col-confidence">
                                  {row.confidenceScore != null
                                    ? `${row.confidenceScore}%`
                                    : "N/A"}
                                </td>
                                <td className="create-entities-table__col-status">
                                  <span
                                    className={`create-entities-status-pill ${
                                      STATUS_CLASS[row.status]
                                    }`}
                                  >
                                    {row.status}
                                  </span>
                                </td>
                                <td className="create-entities-table__col-actions">
                                  <div className="create-entities-row-actions">
                                    <button
                                      type="button"
                                      className="create-entities-action-btn create-entities-action-btn--icon"
                                      aria-label="View"
                                      title="View"
                                      onClick={() =>
                                        openViewDrawer("attributes", row)
                                      }
                                    >
                                      <VisibilityOutlined fontSize="small" />
                                    </button>
                                    <button
                                      type="button"
                                      className="create-entities-action-btn create-entities-action-btn--icon"
                                      aria-label="Modify"
                                      title="Modify"
                                      onClick={() =>
                                        openModifyDrawer("attributes", row)
                                      }
                                    >
                                      <EditOutlined fontSize="small" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="create-entities-table-footer">
                        <span className="create-entities-table-footer__info">
                          Showing {attributeRows.length} of{" "}
                          {attributesPagination.totalRecords} attributes
                        </span>
                        {attributesPagination.isLoadingMore ? (
                          <span className="create-entities-table-footer__info">
                            Loading more…
                          </span>
                        ) : (
                          attributesPagination.page <
                            attributesPagination.totalPages && (
                            <span className="create-entities-table-footer__info">
                              Scroll down to load more…
                            </span>
                          )
                        )}
                      </div>
                    </div>
                  )}

                  {activeTab === "relationships" && (
                    <div className="create-entities-table-wrapper">
                      <div className="create-entities-bulk-bar">
                        <button
                          type="button"
                          className={`create-entities-bulk-bar__select${
                            allSelected
                              ? " create-entities-bulk-bar__select--all"
                              : ""
                          }`}
                          aria-label="Select all"
                          onClick={toggleSelectAll}
                        >
                          {allSelected ? (
                            <CheckOutlined fontSize="inherit" />
                          ) : (
                            "-"
                          )}
                        </button>
                        <span className="create-entities-bulk-bar__label">
                          Select all &bull; {selectedIdsForActiveTab.length}{" "}
                          selected out of {relationshipsPagination.totalRecords}
                        </span>
                      </div>
                      <div
                        className="create-entities-table-scroll"
                        onScroll={handleTableScroll("relationships")}
                      >
                        <table className="create-entities-table">
                          <thead>
                            <tr>
                              <th className="create-entities-table__col-select"></th>
                              <th className="create-entities-table__col-cq">
                                BQ ID
                              </th>
                              <th className="create-entities-table__col-cq">
                                CQ
                              </th>
                              <th className="create-entities-table__col-name">
                                Source Node
                              </th>
                              <th className="create-entities-table__col-type">
                                Relationship Type
                              </th>
                              <th className="create-entities-table__col-name">
                                Target Node
                              </th>
                              <th className="create-entities-table__description">
                                Description
                              </th>
                              <th className="create-entities-table__col-confidence">
                                Confidence Score
                              </th>
                              <th className="create-entities-table__col-status">
                                Status
                              </th>
                              <th className="create-entities-table__col-actions">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {relationshipRows.map((row) => (
                              <tr key={row.id}>
                                <td className="create-entities-table__col-select">
                                  <input
                                    type="checkbox"
                                    checked={relationshipSelectedIds.includes(
                                      row.id
                                    )}
                                    onChange={() => toggleSelectRow(row.id)}
                                  />
                                </td>
                                <td className="create-entities-table__col-cq">
                                  {renderCodeChips(
                                    row.bqCodes,
                                    `${row.id}-bq`,
                                    CQ_CHIP_VISIBLE_LIMIT
                                  )}
                                </td>
                                <td className="create-entities-table__col-cq">
                                  {renderCodeChips(
                                    row.cqCodes,
                                    `${row.id}-cq`,
                                    CQ_CHIP_VISIBLE_LIMIT
                                  )}
                                </td>
                                <td className="create-entities-table__col-name">
                                  <span className="create-entities-marquee">
                                    <span className="create-entities-marquee__text">
                                      {row.sourceNode}
                                    </span>
                                  </span>
                                </td>
                                <td className="create-entities-table__col-type">
                                  <span className="create-entities-marquee">
                                    <span className="create-entities-marquee__text">
                                      {row.relationshipType}
                                    </span>
                                  </span>
                                </td>
                                <td className="create-entities-table__col-name">
                                  <span className="create-entities-marquee">
                                    <span className="create-entities-marquee__text">
                                      {row.targetNode}
                                    </span>
                                  </span>
                                </td>
                                <td
                                  className="create-entities-table__description"
                                  title={row.description}
                                >
                                  {truncateDescription(row.description)}
                                </td>
                                <td className="create-entities-table__col-confidence">
                                  {row.confidenceScore != null
                                    ? `${row.confidenceScore}%`
                                    : "N/A"}
                                </td>
                                <td className="create-entities-table__col-status">
                                  <span
                                    className={`create-entities-status-pill ${
                                      STATUS_CLASS[row.status]
                                    }`}
                                  >
                                    {row.status}
                                  </span>
                                </td>
                                <td className="create-entities-table__col-actions">
                                  <div className="create-entities-row-actions">
                                    <button
                                      type="button"
                                      className="create-entities-action-btn create-entities-action-btn--icon"
                                      aria-label="View"
                                      title="View"
                                      onClick={() =>
                                        openViewDrawer("relationships", row)
                                      }
                                    >
                                      <VisibilityOutlined fontSize="small" />
                                    </button>
                                    <button
                                      type="button"
                                      className="create-entities-action-btn create-entities-action-btn--icon"
                                      aria-label="Modify"
                                      title="Modify"
                                      onClick={() =>
                                        openModifyDrawer("relationships", row)
                                      }
                                    >
                                      <EditOutlined fontSize="small" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="create-entities-table-footer">
                        <span className="create-entities-table-footer__info">
                          Showing {relationshipRows.length} of{" "}
                          {relationshipsPagination.totalRecords} relationships
                        </span>
                        {relationshipsPagination.isLoadingMore ? (
                          <span className="create-entities-table-footer__info">
                            Loading more…
                          </span>
                        ) : (
                          relationshipsPagination.page <
                            relationshipsPagination.totalPages && (
                            <span className="create-entities-table-footer__info">
                              Scroll down to load more…
                            </span>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      <div className="create-entities-footer">
        {/* <button
          type="button"
          className="create-entities-btn create-entities-btn--secondary"
          onClick={handleBack}
        >
          Back
        </button> */}
        <button
          type="button"
          className="create-entities-btn create-entities-btn--primary"
          disabled={
            isTriggeringHarmonization ||
            totalEntitiesCount === 0 ||
            pendingDecisionCount > 0
          }
          title={
            !isTriggeringHarmonization &&
            totalEntitiesCount > 0 &&
            pendingDecisionCount > 0
              ? `${pendingDecisionCount} item${
                  pendingDecisionCount === 1 ? "" : "s"
                } still need to be Accepted or Rejected before continuing.`
              : undefined
          }
          onClick={handleApproveContinue}
        >
          {isTriggeringHarmonization ? (
            "Starting\u2026"
          ) : (
            <>Approve &amp; Continue →</>
          )}
        </button>
        {harmonizationError && (
          <span className="create-entities-view-drawer__error">
            {harmonizationError}
          </span>
        )}
      </div>

      {viewDrawerEntity && (
        <div className="create-entities-view-drawer-root">
          <div
            className="create-entities-view-drawer-overlay"
            onClick={closeViewDrawer}
          />
          <aside className="create-entities-view-drawer">
            <div className="create-entities-view-drawer__header">
              <div className="create-entities-view-drawer__heading">
                <p className="create-entities-view-drawer__title">
                  View {getEntityTypeLabel(viewDrawerEntity.type)}
                </p>
                <p className="create-entities-view-drawer__subtitle">
                  {getViewDrawerSubtitle(
                    viewDrawerEntity.type,
                    viewDrawerEntity.row
                  )}
                </p>
              </div>
              <button
                type="button"
                className="create-entities-view-drawer__close"
                aria-label="Close"
                onClick={closeViewDrawer}
              >
                <CloseOutlined fontSize="small" />
              </button>
            </div>

            <div className="create-entities-view-drawer__traceability">
              <p className="create-entities-view-drawer__section-title">
                Ontology Traceability
              </p>
              <div className="create-entities-view-drawer__trace-chain">
                <span className="create-entities-view-drawer__trace-label">
                  Business Question
                </span>
                <div className="create-entities-view-drawer__trace-bq">
                  {isLoadingViewDrawer
                    ? "Loading…"
                    : viewDrawerTraceability?.businessQuestions.length
                    ? viewDrawerTraceability.businessQuestions.map(
                        (bq, index) => (
                          <React.Fragment key={bq.businessQuestionId ?? index}>
                            <span className="create-entities-view-drawer__bq-tag">
                              [{bq.bqCode}]
                            </span>
                            {bq.questionText && (
                              <p className="create-entities-view-drawer__trace-cq-text">
                                {bq.questionText}
                              </p>
                            )}
                          </React.Fragment>
                        )
                      )
                    : "Not recorded yet"}
                </div>
                <span className="create-entities-view-drawer__trace-arrow">
                  ↓
                </span>
                <span className="create-entities-view-drawer__trace-label">
                  Competency Question
                </span>
                <div className="create-entities-view-drawer__trace-cq">
                  {viewDrawerTraceability?.competencyQuestions.length ? (
                    viewDrawerTraceability.competencyQuestions.map(
                      (cq, index) => (
                        <React.Fragment key={cq.cqId ?? index}>
                          <span className="create-entities-view-drawer__cq-tag">
                            [{cq.cqCode}]
                          </span>
                          {cq.questionText && (
                            <p className="create-entities-view-drawer__trace-cq-text">
                              {cq.questionText}
                            </p>
                          )}
                        </React.Fragment>
                      )
                    )
                  ) : (
                    <span className="create-entities-view-drawer__cq-tag">
                      [{viewDrawerEntity.row.cqCode}]
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="create-entities-view-drawer__body">
              <p className="create-entities-view-drawer__section-title">
                {getEntityTypeLabel(viewDrawerEntity.type)} Details
              </p>
              {viewDrawerEntity.type === "concepts" && (
                <div className="create-entities-view-drawer__field-row">
                  <div className="create-entities-view-drawer__field">
                    <span className="create-entities-view-drawer__field-label">
                      Node Name
                    </span>
                    <div className="create-entities-view-drawer__field-value">
                      {(viewDrawerEntity.row as ConceptRow).nodeName}
                    </div>
                  </div>
                  <div className="create-entities-view-drawer__field">
                    <span className="create-entities-view-drawer__field-label">
                      Node Type
                    </span>
                    <div className="create-entities-view-drawer__field-value">
                      {(viewDrawerEntity.row as ConceptRow).nodeTypeRaw}
                    </div>
                  </div>
                </div>
              )}
              {viewDrawerEntity.type === "attributes" && (
                <div className="create-entities-view-drawer__field">
                  <span className="create-entities-view-drawer__field-label">
                    Attribute Name
                  </span>
                  <div className="create-entities-view-drawer__field-value">
                    {(viewDrawerEntity.row as AttributeRow).attribute}
                  </div>
                </div>
              )}
              {viewDrawerEntity.type === "relationships" && (
                <>
                  <div className="create-entities-view-drawer__relationship-chain">
                    <span className="create-entities-view-drawer__relationship-chain-node">
                      {(viewDrawerEntity.row as RelationshipRow).sourceNode}
                    </span>
                    <span className="create-entities-view-drawer__relationship-chain-arrow">
                      →
                    </span>
                    <span className="create-entities-view-drawer__relationship-chain-type">
                      {
                        (viewDrawerEntity.row as RelationshipRow)
                          .relationshipType
                      }
                    </span>
                    <span className="create-entities-view-drawer__relationship-chain-arrow">
                      →
                    </span>
                    <span className="create-entities-view-drawer__relationship-chain-node">
                      {(viewDrawerEntity.row as RelationshipRow).targetNode}
                    </span>
                  </div>
                  <div className="create-entities-view-drawer__field">
                    <span className="create-entities-view-drawer__field-label">
                      Source Node
                    </span>
                    <div className="create-entities-view-drawer__field-value">
                      {(viewDrawerEntity.row as RelationshipRow).sourceNode}
                    </div>
                  </div>
                  <div className="create-entities-view-drawer__field">
                    <span className="create-entities-view-drawer__field-label">
                      Relationship
                    </span>
                    <div className="create-entities-view-drawer__field-value">
                      {
                        (viewDrawerEntity.row as RelationshipRow)
                          .relationshipType
                      }
                    </div>
                  </div>
                  <div className="create-entities-view-drawer__field">
                    <span className="create-entities-view-drawer__field-label">
                      Target Node
                    </span>
                    <div className="create-entities-view-drawer__field-value">
                      {(viewDrawerEntity.row as RelationshipRow).targetNode}
                    </div>
                  </div>
                </>
              )}
              {viewDrawerEntity.type !== "relationships" && (
                <div className="create-entities-view-drawer__field">
                  <span className="create-entities-view-drawer__field-label">
                    Description
                  </span>
                  <div className="create-entities-view-drawer__field-value create-entities-view-drawer__field-value--multiline">
                    {viewDrawerEntity.row.description}
                  </div>
                </div>
              )}
              {viewDrawerEntity.type === "attributes" && (
                <div className="create-entities-view-drawer__field">
                  <span className="create-entities-view-drawer__field-label">
                    Status
                  </span>
                  <div className="create-entities-view-drawer__field-value">
                    {viewDrawerEntity.row.status}
                  </div>
                </div>
              )}
            </div>

            <div className="create-entities-view-drawer__footer">
              <button
                type="button"
                className="create-entities-view-drawer__close-btn"
                onClick={closeViewDrawer}
              >
                Close
              </button>
            </div>
          </aside>
        </div>
      )}

      {modifyDrawerEntity && (
        <div className="create-entities-view-drawer-root">
          <div
            className="create-entities-view-drawer-overlay"
            onClick={closeModifyDrawer}
          />
          <aside className="create-entities-view-drawer">
            <div className="create-entities-view-drawer__header">
              <div className="create-entities-view-drawer__heading">
                <p className="create-entities-view-drawer__title">
                  Modify {getEntityTypeLabel(modifyDrawerEntity.type)}
                </p>
                <p className="create-entities-view-drawer__subtitle">
                  {getModifyDrawerSubtitle(
                    modifyDrawerEntity.type,
                    modifyDrawerEntity.row
                  )}
                </p>
              </div>
              <button
                type="button"
                className="create-entities-view-drawer__close"
                aria-label="Close"
                onClick={closeModifyDrawer}
              >
                <CloseOutlined fontSize="small" />
              </button>
            </div>

            <div className="create-entities-view-drawer__traceability">
              <p className="create-entities-view-drawer__section-title">
                Ontology Traceability
              </p>
              <div className="create-entities-view-drawer__trace-chain">
                <span className="create-entities-view-drawer__trace-label">
                  Business Question
                </span>
                <div className="create-entities-view-drawer__trace-bq">
                  {isLoadingModifyDrawer
                    ? "Loading…"
                    : modifyDrawerTraceability?.businessQuestions.length
                    ? modifyDrawerTraceability.businessQuestions.map(
                        (bq, index) => (
                          <React.Fragment key={bq.businessQuestionId ?? index}>
                            <span className="create-entities-view-drawer__bq-tag">
                              [{bq.bqCode}]
                            </span>
                            {bq.questionText && (
                              <p className="create-entities-view-drawer__trace-cq-text">
                                {bq.questionText}
                              </p>
                            )}
                          </React.Fragment>
                        )
                      )
                    : "Not recorded yet"}
                </div>
                <span className="create-entities-view-drawer__trace-arrow">
                  ↓
                </span>
                <span className="create-entities-view-drawer__trace-label">
                  Competency Question
                </span>
                <div className="create-entities-view-drawer__trace-cq">
                  {modifyDrawerTraceability?.competencyQuestions.length ? (
                    modifyDrawerTraceability.competencyQuestions.map(
                      (cq, index) => (
                        <React.Fragment key={cq.cqId ?? index}>
                          <span className="create-entities-view-drawer__cq-tag">
                            [{cq.cqCode}]
                          </span>
                          {cq.questionText && (
                            <p className="create-entities-view-drawer__trace-cq-text">
                              {cq.questionText}
                            </p>
                          )}
                        </React.Fragment>
                      )
                    )
                  ) : (
                    <span className="create-entities-view-drawer__cq-tag">
                      [{modifyDrawerEntity.row.cqCode}]
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="create-entities-view-drawer__body">
              <p className="create-entities-view-drawer__section-title">
                {modifyDrawerEntity.type === "relationships"
                  ? "Relationship Details"
                  : "Entity Information"}
              </p>
              {modifyDrawerEntity.type === "concepts" && (
                <>
                  <div className="create-entities-view-drawer__field-row">
                    <div className="create-entities-view-drawer__field">
                      <span className="create-entities-view-drawer__field-label">
                        Node Name *
                      </span>
                      <input
                        type="text"
                        className="create-entities-view-drawer__field-input"
                        value={modifyForm.nodeName ?? ""}
                        onChange={(event) =>
                          updateModifyForm("nodeName", event.target.value)
                        }
                      />
                    </div>
                    <div className="create-entities-view-drawer__field">
                      <span className="create-entities-view-drawer__field-label">
                        Node Type *
                      </span>
                      <input
                        type="text"
                        className="create-entities-view-drawer__field-input"
                        value={modifyForm.nodeType ?? ""}
                        onChange={(event) =>
                          updateModifyForm("nodeType", event.target.value)
                        }
                      />
                    </div>
                  </div>
                  {/* <div className="create-entities-view-drawer__field">
                    <span className="create-entities-view-drawer__field-label">
                      Status
                    </span>
                    <select
                      className="create-entities-view-drawer__field-select"
                      value={modifyForm.status ?? "Needs Review"}
                      onChange={(event) =>
                        updateModifyForm(
                          "status",
                          event.target.value as ConceptStatus
                        )
                      }
                    >
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div> */}
                  <div className="create-entities-view-drawer__field">
                    <span className="create-entities-view-drawer__field-label">
                      Description *
                    </span>
                    <textarea
                      className="create-entities-view-drawer__field-textarea"
                      value={modifyForm.description ?? ""}
                      onChange={(event) =>
                        updateModifyForm("description", event.target.value)
                      }
                    />
                  </div>
                </>
              )}
              {modifyDrawerEntity.type === "attributes" && (
                <>
                  <div className="create-entities-view-drawer__field">
                    <span className="create-entities-view-drawer__field-label">
                      Attribute Name *
                    </span>
                    <input
                      type="text"
                      className="create-entities-view-drawer__field-input"
                      value={modifyForm.attribute ?? ""}
                      onChange={(event) =>
                        updateModifyForm("attribute", event.target.value)
                      }
                    />
                  </div>
                  {/* <div className="create-entities-view-drawer__field">
                    <span className="create-entities-view-drawer__field-label">
                      Status
                    </span>
                    <select
                      className="create-entities-view-drawer__field-select"
                      value={modifyForm.status ?? "Needs Review"}
                      onChange={(event) =>
                        updateModifyForm(
                          "status",
                          event.target.value as ConceptStatus
                        )
                      }
                    >
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div> */}
                  <div className="create-entities-view-drawer__field">
                    <span className="create-entities-view-drawer__field-label">
                      Description *
                    </span>
                    <textarea
                      className="create-entities-view-drawer__field-textarea"
                      value={modifyForm.description ?? ""}
                      onChange={(event) =>
                        updateModifyForm("description", event.target.value)
                      }
                    />
                  </div>
                </>
              )}
              {modifyDrawerEntity.type === "relationships" && (
                <>
                  <div className="create-entities-view-drawer__relationship-chain">
                    <span className="create-entities-view-drawer__relationship-chain-node">
                      {modifyForm.sourceNode}
                    </span>
                    <span className="create-entities-view-drawer__relationship-chain-arrow">
                      →
                    </span>
                    <span className="create-entities-view-drawer__relationship-chain-type">
                      {modifyForm.relationshipType}
                    </span>
                    <span className="create-entities-view-drawer__relationship-chain-arrow">
                      →
                    </span>
                    <span className="create-entities-view-drawer__relationship-chain-node">
                      {modifyForm.targetNode}
                    </span>
                  </div>
                  <div className="create-entities-view-drawer__field">
                    <span className="create-entities-view-drawer__field-label">
                      Source Node *
                    </span>
                    <select
                      className="create-entities-view-drawer__field-select"
                      value={modifyForm.sourceNode ?? ""}
                      onChange={(event) =>
                        updateModifyForm("sourceNode", event.target.value)
                      }
                    >
                      {conceptRows.map((conceptRow) => (
                        <option key={conceptRow.id} value={conceptRow.nodeName}>
                          {conceptRow.nodeName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="create-entities-view-drawer__field">
                    <span className="create-entities-view-drawer__field-label">
                      Relationship *
                    </span>
                    <input
                      type="text"
                      className="create-entities-view-drawer__field-input"
                      value={modifyForm.relationshipType ?? ""}
                      onChange={(event) =>
                        updateModifyForm("relationshipType", event.target.value)
                      }
                    />
                  </div>
                  <div className="create-entities-view-drawer__field">
                    <span className="create-entities-view-drawer__field-label">
                      Target Node *
                    </span>
                    <select
                      className="create-entities-view-drawer__field-select"
                      value={modifyForm.targetNode ?? ""}
                      onChange={(event) =>
                        updateModifyForm("targetNode", event.target.value)
                      }
                    >
                      {conceptRows.map((conceptRow) => (
                        <option key={conceptRow.id} value={conceptRow.nodeName}>
                          {conceptRow.nodeName}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>

            <div className="create-entities-view-drawer__footer create-entities-view-drawer__footer--split">
              <button
                type="button"
                className="create-entities-view-drawer__cancel-btn"
                onClick={closeModifyDrawer}
              >
                Cancel
              </button>
              <div className="create-entities-view-drawer__footer-actions">
                {/* <button
                  type="button"
                  className="create-entities-view-drawer__cancel-btn"
                  disabled={isSavingModify}
                  onClick={() => saveModifyDrawer(true)}
                >
                  Save Draft
                </button> */}
                <button
                  type="button"
                  className="create-entities-view-drawer__apply-btn"
                  disabled={isSavingModify}
                  onClick={() => saveModifyDrawer(false)}
                >
                  Apply Changes
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}

      {isAddDrawerOpen && (
        <div className="create-entities-view-drawer-root">
          <div
            className="create-entities-view-drawer-overlay"
            onClick={closeAddDrawer}
          />
          <aside className="create-entities-view-drawer">
            <div className="create-entities-view-drawer__header">
              <div className="create-entities-view-drawer__heading">
                <p className="create-entities-view-drawer__title">
                  {getAddDrawerTitle(addItemType)}
                </p>
                <p className="create-entities-view-drawer__subtitle">
                  {getAddDrawerSubtitle(addItemType)}
                </p>
              </div>
              <button
                type="button"
                className="create-entities-view-drawer__close"
                aria-label="Close"
                onClick={closeAddDrawer}
              >
                <CloseOutlined fontSize="small" />
              </button>
            </div>

            <div className="create-entities-view-drawer__traceability">
              <p className="create-entities-view-drawer__section-title">
                Ontology Traceability
              </p>
              <div className="create-entities-view-drawer__trace-chain">
                <span className="create-entities-view-drawer__trace-label">
                  Business Question
                </span>
                <MultiSelectDropdown
                  placeholder="Select Business Questions"
                  options={businessQuestions.map((bq) => ({
                    value: bq.businessQuestionId,
                    label: bq.questionText
                  }))}
                  selectedValues={addForm.businessQuestionIds ?? []}
                  onChange={(selectedBqIds) => {
                    const allowedCqIds = getCompetencyQuestionsForBqs(
                      selectedBqIds
                    ).map((cq) => cq.id);
                    setAddForm((prev) => ({
                      ...prev,
                      businessQuestionIds: selectedBqIds,
                      competencyQuestionIds: (
                        prev.competencyQuestionIds ?? []
                      ).filter((cqId) => allowedCqIds.includes(cqId))
                    }));
                  }}
                />
                <span className="create-entities-view-drawer__trace-arrow">
                  ↓
                </span>
                <span className="create-entities-view-drawer__trace-label">
                  Competency Question
                </span>
                <MultiSelectDropdown
                  placeholder="Select Competency Questions"
                  emptyMessage="Select a Business Question first"
                  disabled={!addForm.businessQuestionIds?.length}
                  options={getCompetencyQuestionsForBqs(
                    addForm.businessQuestionIds
                  ).map((cq) => ({ value: cq.id, label: cq.question }))}
                  selectedValues={addForm.competencyQuestionIds ?? []}
                  onChange={(selectedCqIds) =>
                    updateAddForm("competencyQuestionIds", selectedCqIds)
                  }
                />
                <p className="create-entities-view-drawer__trace-cq-text">
                  Competency Questions are filtered based on the selected
                  Business Questions.
                </p>
              </div>
            </div>

            <div className="create-entities-view-drawer__body">
              {addItemType === "concepts" && (
                <>
                  <p className="create-entities-view-drawer__section-title">
                    Entity Information
                  </p>
                  <div className="create-entities-view-drawer__field-row">
                    <div className="create-entities-view-drawer__field">
                      <span className="create-entities-view-drawer__field-label">
                        Node Name *
                      </span>
                      <input
                        type="text"
                        className="create-entities-view-drawer__field-input"
                        placeholder="Enter node name"
                        value={addForm.nodeName ?? ""}
                        onChange={(event) =>
                          updateAddForm("nodeName", event.target.value)
                        }
                      />
                    </div>
                    <div className="create-entities-view-drawer__field">
                      <span className="create-entities-view-drawer__field-label">
                        Node Type *
                      </span>
                      <input
                        type="text"
                        className="create-entities-view-drawer__field-input"
                        value={addForm.nodeType ?? ""}
                        onChange={(event) =>
                          updateAddForm("nodeType", event.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="create-entities-view-drawer__field">
                    <span className="create-entities-view-drawer__field-label">
                      Description *
                    </span>
                    <textarea
                      className="create-entities-view-drawer__field-textarea"
                      placeholder="Describe the ontology entity…"
                      value={addForm.description ?? ""}
                      onChange={(event) =>
                        updateAddForm("description", event.target.value)
                      }
                    />
                  </div>
                </>
              )}
              {addItemType === "attributes" && (
                <>
                  <p className="create-entities-view-drawer__section-title">
                    Attribute Details
                  </p>
                  <div className="create-entities-view-drawer__field">
                    <span className="create-entities-view-drawer__field-label">
                      Node Name *
                    </span>
                    <select
                      className="create-entities-view-drawer__field-select"
                      value={addForm.nodeId ?? ""}
                      onChange={(event) =>
                        updateAddForm("nodeId", event.target.value)
                      }
                    >
                      <option value="">Select owning node</option>
                      {conceptRows.map((conceptRow) => (
                        <option key={conceptRow.id} value={conceptRow.id}>
                          {conceptRow.nodeName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="create-entities-view-drawer__field">
                    <span className="create-entities-view-drawer__field-label">
                      Attribute Name *
                    </span>
                    <input
                      type="text"
                      className="create-entities-view-drawer__field-input"
                      placeholder="Enter attribute name"
                      value={addForm.attribute ?? ""}
                      onChange={(event) =>
                        updateAddForm("attribute", event.target.value)
                      }
                    />
                  </div>
                  <div className="create-entities-view-drawer__field">
                    <span className="create-entities-view-drawer__field-label">
                      Description *
                    </span>
                    <textarea
                      className="create-entities-view-drawer__field-textarea"
                      placeholder="Describe the attribute"
                      value={addForm.description ?? ""}
                      onChange={(event) =>
                        updateAddForm("description", event.target.value)
                      }
                    />
                  </div>
                </>
              )}
              {addItemType === "relationships" && (
                <>
                  <p className="create-entities-view-drawer__section-title">
                    Relationship Details
                  </p>
                  <div className="create-entities-view-drawer__field">
                    <span className="create-entities-view-drawer__field-label">
                      Source Node *
                    </span>
                    <select
                      className="create-entities-view-drawer__field-select"
                      value={addForm.sourceNode ?? ""}
                      onChange={(event) =>
                        updateAddForm("sourceNode", event.target.value)
                      }
                    >
                      <option value="">Select Source Node</option>
                      {conceptRows.map((conceptRow) => (
                        <option key={conceptRow.id} value={conceptRow.nodeName}>
                          {conceptRow.nodeName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="create-entities-view-drawer__field">
                    <span className="create-entities-view-drawer__field-label">
                      Relationship Type *
                    </span>
                    <input
                      type="text"
                      className="create-entities-view-drawer__field-input"
                      placeholder="Select Relationship Type"
                      value={addForm.relationshipType ?? ""}
                      onChange={(event) =>
                        updateAddForm("relationshipType", event.target.value)
                      }
                    />
                  </div>
                  <div className="create-entities-view-drawer__field">
                    <span className="create-entities-view-drawer__field-label">
                      Target Node *
                    </span>
                    <select
                      className="create-entities-view-drawer__field-select"
                      value={addForm.targetNode ?? ""}
                      onChange={(event) =>
                        updateAddForm("targetNode", event.target.value)
                      }
                    >
                      <option value="">Search Target Node</option>
                      {conceptRows.map((conceptRow) => (
                        <option key={conceptRow.id} value={conceptRow.nodeName}>
                          {conceptRow.nodeName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="create-entities-view-drawer__field">
                    <span className="create-entities-view-drawer__field-label">
                      Description *
                    </span>
                    <textarea
                      className="create-entities-view-drawer__field-textarea"
                      placeholder="Describe the relationship"
                      value={addForm.description ?? ""}
                      onChange={(event) =>
                        updateAddForm("description", event.target.value)
                      }
                    />
                  </div>
                </>
              )}
              {addEntityError && (
                <span className="create-entities-view-drawer__error">
                  {addEntityError}
                </span>
              )}
            </div>

            <div className="create-entities-view-drawer__footer create-entities-view-drawer__footer--split">
              <button
                type="button"
                className="create-entities-view-drawer__cancel-btn"
                onClick={closeAddDrawer}
              >
                Cancel
              </button>
              <div className="create-entities-view-drawer__footer-actions">
                {/* <button
                  type="button"
                  className="create-entities-view-drawer__cancel-btn"
                  disabled={isCreatingEntity}
                  onClick={saveAddDrawer}
                >
                  Save Draft
                </button> */}
                <button
                  type="button"
                  className="create-entities-view-drawer__apply-btn"
                  disabled={isCreatingEntity}
                  onClick={saveAddDrawer}
                >
                  {addItemType === "concepts"
                    ? "Create Entity"
                    : addItemType === "attributes"
                    ? "Create Attribute"
                    : "Create Relationship"}
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default CreateOntologyEntitiesRelationships;
